import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, forkJoin, Subscription, interval, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, takeWhile, finalize, shareReplay, startWith, filter, take, delay } from 'rxjs/operators';
import { Loan, Document, type File, Attribute, TruthValue, ValidationDetail, ValidationDetailTable, AdminDocument, DocumentKey, DocumentKeysResponse, DocumentKeyRequest, EvaluationFile, EvaluationFilesResponse, AttributesResponse, EvaluationStatus, EvaluationFileResult, FieldResult, EvaluationRunsResponse, DashboardResponse, EvalSummaryResponse, ExtractionReportResponse, ExtractionSubmitResponse, ExtractionPollResponse, DataExtractedAttribute, ActiveEvaluation, ActiveRunsResponse, ExtractionRequestDetailsQuery, ExtractionRequestDetailsResponse, Client, SequencingRequestDetailsQuery, SequencingRequestDetailsResponse, SequencingPollSequenceQuery, SequencingPollSequenceResponse, SequencingExecutionDataQuery, SequencingDocumentTypesQuery, SequencingDocumentTypeItem, TuneSaveAnnotationsRequest, TuneSaveAnnotationsResponse, TuneSourcePdfUrlResponse, TableData } from '../models/truth-management.model';
import { 
  FieldSchemaResponse, 
  FieldSchemaField, 
  FieldMetadata 
} from '../models/truth-management.model';
import { environment } from '../../environments/environment';
import { NotificationService } from '../services/notification.service';

/**
 * Event emitted when an evaluation completes (COMPLETED or FAILED)
 */
export interface EvaluationCompletionEvent {
  evaluationId: string;
  documentModel: string;
  status: 'COMPLETED' | 'FAILED';
  runType: 'all' | 'part';
  failedFiles?: number;
}

/**
 * Event emitted when a file's status changes (cascades from file to document model status)
 */
export interface FileStatusChangedEvent {
  documentModel: string;
  fileId: number | string;
  statusMetadata: any; // StatusMetadata interface
}

export interface AiSuggestMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface AiSuggestResponse {
  role?: 'assistant';
  content?: string;
  status?: string;
  questions?: string[];
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class TruthManagement implements OnDestroy {
  // API Configuration - uses environment.apiBaseUrl so it automatically routes to correct environment
  // Most endpoints use api/v1, only bulk and get attributes APIs use api/v2
  private apiUrlV1 = `${environment.apiBaseUrl}/api/v1`;
  private apiUrlV2 = `${environment.apiBaseUrl}/api/v2`;
  // Note: Auth token is now handled by AuthService and HTTP Interceptor

  private selectedLoanSubject = new BehaviorSubject<Loan | null>(null);
  private selectedDocumentSubject = new BehaviorSubject<Document | null>(null);
  private selectedViewSubject = new BehaviorSubject<'files' | 'attributes'>('files');
  
  // ============================================
  // CENTRALIZED STATE MANAGEMENT (Unified Service)
  // ============================================
  
  // Centralized state for all active evaluations
  private activeEvaluationsSubject = new BehaviorSubject<Map<string, ActiveEvaluation>>(new Map());
  public activeEvaluations$ = this.activeEvaluationsSubject.asObservable();

  // Track polling subscriptions
  private pollingSubscriptions = new Map<string, Subscription>();
  private readonly POLLING_INTERVAL = 5000; // 5 seconds
  private readonly MAX_CONSECUTIVE_ERRORS = 3; // Stop polling after 3 consecutive errors
  
  // Track consecutive errors per evaluation
  private errorCounts = new Map<string, number>();

  // Completion event emitter - notifies components when evaluation completes
  private completionSubject = new Subject<EvaluationCompletionEvent>();
  public evaluationCompleted$ = this.completionSubject.asObservable();
  
  // Centralized timer management - all components can subscribe to live timer updates
  private timersSubject = new BehaviorSubject<Map<string, string>>(new Map());
  public timers$ = this.timersSubject.asObservable();
  private timerIntervals = new Map<string, any>();
  
  // ? Data change notifications - notify when truth values change
  private dataChangedSubject = new Subject<string>();
  public dataChanged$ = this.dataChangedSubject.asObservable();
  
  // File status change notifications - cascade file status to document model status
  private fileStatusChangedSubject = new Subject<FileStatusChangedEvent>();
  public fileStatusChanged$ = this.fileStatusChangedSubject.asObservable();
  
  // Extraction completion events - notify when background extraction completes
  private extractionCompletedSubject = new Subject<{fileId: number | string; success: boolean}>();
  public extractionCompleted$ = this.extractionCompletedSubject.asObservable();
  
  // ============================================
  // DASHBOARD CACHING (Prevent duplicate API calls)
  // ============================================
  
  // Cached dashboard observable - shares data across components
  private dashboardCacheSubject = new BehaviorSubject<DashboardResponse | null>(null);
  public dashboardCache$ = this.dashboardCacheSubject.asObservable();
  private dashboardLoading = false;
  private readonly DASHBOARD_CACHE_KEY = 'truthManagement_dashboardCache_v1';

  // Cached eval summary observable - dedicated cache for loan validation summary endpoint
  private evalSummaryCacheSubject = new BehaviorSubject<EvalSummaryResponse | null>(null);

  // ============================================
  // EVALUATION RUNS CACHING (Stale-While-Revalidate, 5-min TTL)
  // ============================================
  // In-memory cache keyed by "documentModel:runType" — shows instant data on back-navigation.
  // Entries expire after 5 minutes so long sessions never serve indefinitely stale data.
  private evaluationRunsCache = new Map<string, EvaluationRunsResponse>();
  private evaluationRunsCacheTimestamps = new Map<string, number>();
  private readonly EVAL_RUNS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  /** In-flight / session cache for eval/status — keyed by evaluationId.
   *  shareReplay(1) ensures concurrent callers share one HTTP request.
   *  COMPLETED/FAILED entries stay forever (immutable). RUNNING/PENDING entries
   *  expire after 3 s so the next polling cycle fetches fresh progress data. */
  private evaluationStatusCache = new Map<string, Observable<EvaluationStatus>>();

  // ── Document keys cache ────────────────────────────────────────────────────
  // Caches the in-flight/resolved Observable so concurrent callers within a
  // single page load share one HTTP request (shareReplay(1)).
  // No TTL — entries are cleared by invalidateDocumentModelCache() which is
  // called from ngOnDestroy (navigate-away) and all schema-mutation points.
  private documentKeysCache = new Map<string, Observable<DocumentKeysResponse>>();

  // ── Document schema cache ──────────────────────────────────────────────────
  // Same strategy as documentKeysCache above — no TTL, cleared on navigate-away.
  private documentSchemaCache = new Map<string, Observable<any>>();

  // ── Field schema cache ─────────────────────────────────────────────────────
  // Same strategy as documentKeysCache — no TTL, cleared on navigate-away.
  private fieldSchemaCache = new Map<string, Observable<any>>();

  // ── Client-based API caches (Per-client data caching) ────────────────────
  // These caches store results keyed by clientId (or clientId + date range).
  // When user switches clients and returns, data is served from cache instantly.
  // Uses shareReplay(1) so concurrent calls share one HTTP request.
  private evalSummaryCache = new Map<string, Observable<EvalSummaryResponse>>();
  private extractionReportCache = new Map<string, Observable<ExtractionReportResponse>>();

  // ── Service-level evaluation status store ────────────────────────────────
  // Keyed by evaluationId. Components write here after fetching status;
  // other components can read synchronously instead of making a second HTTP call.
  private latestEvalStatusMapSubject = new BehaviorSubject<Map<string, EvaluationStatus>>(new Map());
  public readonly latestEvalStatusMap$ = this.latestEvalStatusMapSubject.asObservable();

  /** Store a freshly-fetched status so other components can read it without re-fetching. */
  updateLatestEvalStatus(evaluationId: string, status: EvaluationStatus): void {
    const current = new Map(this.latestEvalStatusMapSubject.getValue());
    current.set(evaluationId, status);
    this.latestEvalStatusMapSubject.next(current);
  }

  /** Synchronously read a previously stored status (returns undefined if not yet cached). */
  getLatestEvalStatus(evaluationId: string): EvaluationStatus | undefined {
    return this.latestEvalStatusMapSubject.getValue().get(evaluationId);
  }

  /**
   * Synchronously return the first RUNNING/PENDING active evaluation for a document model.
   * Reads the BehaviorSubject directly — zero HTTP calls, zero async latency.
   * Returns undefined when nothing is active (COMPLETED/FAILED entries are ignored).
   * Use this in component init to avoid re-discovering state the service already holds.
   */
  getActiveEvaluationForModel(documentModel: string): ActiveEvaluation | undefined {
    const map = this.activeEvaluationsSubject.getValue();
    for (const runType of ['all', 'part'] as const) {
      const entry = map.get(`${documentModel}:${runType}`);
      if (entry && entry.status?.status !== 'COMPLETED' && entry.status?.status !== 'FAILED') {
        return entry;
      }
    }
    return undefined;
  }

  /**
   * Clear all in-memory caches and reset deduplication state.
   * Call on logout so a subsequent login never sees another user's data.
   * Does NOT stop polling or clear activeEvaluations$ — those are cleared
   * by clearActiveRun() when polling finishes and by sessionStorage removal on logout.
   */
  clearAllCaches(): void {
    this.setDashboardCache(null);
    this.evalSummaryCacheSubject.next(null);
    this.dashboardLoading = false;
    this.evaluationRunsCache.clear();
    this.evaluationRunsCacheTimestamps.clear();
    this.evaluationStatusCache.clear();
    this.documentKeysCache.clear();
    this.documentSchemaCache.clear();
    this.fieldSchemaCache.clear();
    this.latestEvalStatusMapSubject.next(new Map());
    // Reset completion deduplication so a re-login can re-emit events
    // for evaluations that may still be referenced in sessionStorage.
    this.completedEvaluationIds.clear();
  }

  /**
   * Invalidate document-keys and schema caches for a specific document model.
   * Call this after any operation that mutates the document model schema
   * (schema import, field create/update/delete) so the next visit to Truth
   * Management fetches fresh data from the backend instead of serving stale cache.
   */
  invalidateDocumentModelCache(documentModel: string): void {
    this.documentKeysCache.delete(documentModel);
    this.documentSchemaCache.delete(documentModel);
    this.fieldSchemaCache.delete(documentModel);
  }

  getCachedEvaluationRuns(documentModel: string, runType: 'all' | 'part'): EvaluationRunsResponse | undefined {
    const key = `${documentModel}:${runType}`;
    const cachedAt = this.evaluationRunsCacheTimestamps.get(key);
    if (!cachedAt || (Date.now() - cachedAt) >= this.EVAL_RUNS_CACHE_TTL_MS) {
      // Entry is absent or expired — treat as cache miss
      return undefined;
    }
    return this.evaluationRunsCache.get(key);
  }

  cacheEvaluationRuns(documentModel: string, runType: 'all' | 'part', response: EvaluationRunsResponse): void {
    const key = `${documentModel}:${runType}`;
    this.evaluationRunsCache.set(key, response);
    this.evaluationRunsCacheTimestamps.set(key, Date.now());
  }
  
  /**
   * Show extraction notification with override count (called by component after calculating accurate count)
   * This method is global and works on ANY page in the application
   */
  showExtractionNotification(fileId: number | string, documentName: string, fileName: string, fieldCount: number, overrideCount: number): void {
    if (fieldCount > 0) {
      this.notificationService.showAutoFillSuccess(
        documentName,
        fileName,
        fieldCount,
        overrideCount > 0 ? overrideCount : undefined
      );
    } else {
      this.notificationService.showError('No matching fields were extracted from the document.');
    }
  }
  
  /**
   * Notify components that truth data has changed
   * Used to refresh Loan Validation dashboard when Truth Management saves data
   */
  notifyDataChanged(documentCode?: string): void {
    this.dataChangedSubject.next(documentCode || '');
  }

  /**
   * Notify components that a file's status has changed
   * Cascades from file status update to trigger ground truth status recalculation in loan-validation
   */
  notifyFileStatusChanged(event: FileStatusChangedEvent): void {
    this.fileStatusChangedSubject.next(event);
  }
  
  // ============================================
  // CONCURRENT RUN PREVENTION (Race Condition Fix)
  // ============================================
  
  // Track in-flight API requests to prevent duplicate runs
  private pendingRunRequests = new Map<string, Observable<any>>();
  
  // Track completed evaluation IDs to prevent duplicate completion events
  private completedEvaluationIds = new Set<string>();
  private readonly COMPLETION_MEMORY_DURATION = 30000; // 30 seconds
  
  constructor(private http: HttpClient,
              private notificationService: NotificationService,
              private ngZone: NgZone) {
    // Restore active evaluations from sessionStorage (survives page refresh)
    this.restoreActiveEvaluations();
    this.restoreDashboardCache();
  }

  private restoreDashboardCache(): void {
    try {
      const raw = sessionStorage.getItem(this.DASHBOARD_CACHE_KEY);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as DashboardResponse;
      if (parsed && Array.isArray(parsed.document_models) && parsed.document_models.length > 0) {
        this.dashboardCacheSubject.next(parsed);
      }
    } catch (error) {
      sessionStorage.removeItem(this.DASHBOARD_CACHE_KEY);
    }
  }

  private setDashboardCache(cache: DashboardResponse | null): void {
    this.dashboardCacheSubject.next(cache);

    if (!cache) {
      sessionStorage.removeItem(this.DASHBOARD_CACHE_KEY);
      return;
    }

    try {
      sessionStorage.setItem(this.DASHBOARD_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
    }
  }

  /**
   * Run extraction for a given document model and file blob, store pending results
   * in sessionStorage keyed by fileId so any component can pick them up later.
   * This allows extraction to be started from anywhere in the app.
   */
  runExtractionAndStorePendingResult(documentModel: string, fileId: number | string, fileBlob: Blob | globalThis.File, documentName?: string, fileName?: string): Observable<void> {
    return this.submitDataExtraction(documentModel, fileBlob, fileName).pipe(
      switchMap((submitResp: any) => {
        const requestId = submitResp.request_id;
        // Poll until COMPLETED and take the first completed response
        return interval(this.POLLING_INTERVAL).pipe(
          startWith(0),
          switchMap(() => this.pollExtractionStatus(requestId)),
          takeWhile((resp: any) => !!resp && resp.status !== 'COMPLETED' && resp.status !== 'FAILED', true),
          filter((resp: any) => !!resp && (resp.status === 'COMPLETED' || resp.status === 'FAILED')),
          take(1),
          map((completedResp: any) => {
            // Handle FAILED status — blank/flowchart PDFs
            if (completedResp.status === 'FAILED') {
              const notifFileName = fileName || `File ${fileId}`;
              const notifDocName = documentName || documentModel;
              this.notificationService.showAutoFillError(
                notifDocName,
                notifFileName,
                'Autofill not applicable.',
                'Error',
                'Extracted values are not available'
              );
              return void 0;
            }

            const attributes = completedResp.attributes || {};
            // Store in sessionStorage so the truth-management component (or any) can apply later
            const pendingKey = `truthManagement_pendingResults_${fileId}`;
            const pendingData = {
              fileId,
              extractedAttrs: attributes,
              timestamp: Date.now(),
              isOverrideMode: false
            };
            try {
              sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
            } catch (err) {
            }

            // Count fields with actual non-empty values
            let actualFieldCount = 0;
            for (const [key, value] of Object.entries(attributes)) {
              if (Array.isArray(value) && value.length > 0) {
                const firstItem = value[0];
                const extractedValue = firstItem?.Value || firstItem?.value || '';
                if (extractedValue && extractedValue.toString().trim()) {
                  actualFieldCount++;
                }
              } else if (value && typeof value === 'object') {
                const extractedValue = (value as any).Value || (value as any).value || '';
                if (extractedValue && extractedValue.toString().trim()) {
                  actualFieldCount++;
                }
              }
            }

            // Notify user based on actual extracted values
            const notifFileName = fileName || `File ${fileId}`;
            const notifDocName = documentName || documentModel;
            if (actualFieldCount > 0) {
              this.notificationService.showAutoFillSuccess(notifDocName, notifFileName, actualFieldCount, undefined);
            } else {
              this.notificationService.showAutoFillError(
                notifDocName,
                notifFileName,
                'Autofill not applicable.',
                'Reason',
                'Technical error'
              );
            }

            return void 0;
          })
        );
      })
    );
  }

  /**
   * Start extraction in background and manage polling internally so it continues
   * even if the calling component is destroyed or user navigates away.
   */
  startExtractionInBackground(documentModel: string, fileId: number | string, fileBlob: Blob | globalThis.File, documentName?: string, fileName?: string, isOverrideMode?: boolean, onComplete?: () => void): void {
    this.submitDataExtraction(documentModel, fileBlob, fileName).subscribe({
      next: (submitResp: any) => {
        const requestId = submitResp.request_id;
        // Persist extraction state for resume on refresh/navigation
        // Store ALL active extractions, not just one
        try {
          const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
          extractions[fileId.toString()] = {
            timestamp: Date.now(),
            documentCode: documentModel,
            fileId,
            fileName: fileName || `File ${fileId}`,
            requestId,
            isOverrideMode: isOverrideMode || false
          };
          localStorage.setItem('truthManagement_extractions', JSON.stringify(extractions));
        } catch (err) {
        }

        // Start polling in background
        const pollingSub = interval(this.POLLING_INTERVAL).pipe(
          startWith(0),
          switchMap(() => this.pollExtractionStatus(requestId)),
          takeWhile((resp: any) => !!resp && resp.status !== 'COMPLETED' && resp.status !== 'FAILED', true),
          filter((resp: any) => !!resp && (resp.status === 'COMPLETED' || resp.status === 'FAILED')),
          take(1)
        ).subscribe({
          next: (completedResp: any) => {
            // Handle FAILED status — blank/flowchart PDFs
            if (completedResp.status === 'FAILED') {
              const notifFileName = fileName || `File ${fileId}`;
              const notifDocName = documentName || documentModel;
              this.notificationService.showAutoFillError(
                notifDocName,
                notifFileName,
                'Autofill not applicable.',
                'Error',
                'Extracted values are not available'
              );
              // Cleanup persisted extraction state
              try {
                const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
                delete extractions[fileId.toString()];
                if (Object.keys(extractions).length > 0) {
                  localStorage.setItem('truthManagement_extractions', JSON.stringify(extractions));
                } else {
                  localStorage.removeItem('truthManagement_extractions');
                }
              } catch (_) {}
              this.pollingSubscriptions.delete(requestId);
              this.extractionCompletedSubject.next({fileId, success: false});
              if (onComplete) { onComplete(); }
              return;
            }

            const attributes = completedResp.attributes || {};
            if (Object.keys(attributes).length > 0) {
            }
            
            // Restore isOverrideMode from localStorage extraction state
            let isOverrideMode = false;
            try {
              const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
              const extractionState = extractions[fileId.toString()];
              if (extractionState && extractionState.isOverrideMode !== undefined) {
                isOverrideMode = extractionState.isOverrideMode;
              }
            } catch (err) {
            }
            
            const pendingKey = `truthManagement_pendingResults_${fileId}`;
            const pendingData = { fileId, extractedAttrs: attributes, timestamp: Date.now(), isOverrideMode: isOverrideMode };
            try {
              sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
            } catch (err) {
            }

            // Count fields and calculate override count if in override mode
            let actualFieldCount = 0;
            let overriddenCount = 0;
            
            if (isOverrideMode) {
              // Try to read existing values from sessionStorage to calculate override count
              try {
                const modifiedValuesKey = `truthManagement_modifiedValues_${fileId}`;
                const truthValuesKey = `truthManagement_truthValues_${fileId}`;
                const existingModifiedStr = sessionStorage.getItem(modifiedValuesKey);
                const existingTruthStr = sessionStorage.getItem(truthValuesKey);
                const existingModified = existingModifiedStr ? JSON.parse(existingModifiedStr) : {};
                const existingTruth = existingTruthStr ? JSON.parse(existingTruthStr) : {};
                
                for (const [key, value] of Object.entries(attributes)) {
                  let extractedValue = '';
                  if (Array.isArray(value) && value.length > 0) {
                    const firstItem = value[0];
                    extractedValue = firstItem?.Value || firstItem?.value || '';
                  } else if (value && typeof value === 'object') {
                    extractedValue = (value as any).Value || (value as any).value || '';
                  }
                  
                  if (extractedValue && extractedValue.toString().trim()) {
                    actualFieldCount++;
                    
                    // Check if this field had existing value
                    const hasExistingModified = existingModified[key] && existingModified[key].trim && existingModified[key].trim();
                    const hasExistingTruth = existingTruth[key] && existingTruth[key].value && existingTruth[key].value.trim && existingTruth[key].value.trim();
                    
                    if (hasExistingModified || hasExistingTruth) {
                      overriddenCount++;
                    }
                  }
                }
              } catch (err) {
              }
            } else {
              // Normal mode - just count fields
              for (const [key, value] of Object.entries(attributes)) {
                if (Array.isArray(value) && value.length > 0) {
                  const firstItem = value[0];
                  const extractedValue = firstItem?.Value || firstItem?.value || '';
                  if (extractedValue && extractedValue.toString().trim()) {
                    actualFieldCount++;
                  }
                } else if (value && typeof value === 'object') {
                  const extractedValue = (value as any).Value || (value as any).value || '';
                  if (extractedValue && extractedValue.toString().trim()) {
                    actualFieldCount++;
                  }
                }
              }
            }

            // Immediately show error notification if no values were extracted
            if (actualFieldCount === 0) {
              const notifFileName = fileName || `File ${fileId}`;
              const notifDocName = documentName || documentModel;
              this.notificationService.showAutoFillError(
                notifDocName,
                notifFileName,
                'Autofill not applicable.',
                'Error',
                'Extracted values are not available'
              );
            }

            // Fallback: If component doesn't show notification (user navigated away), service shows it
            // Wait 2 seconds for component to show notification, then check if it was shown
            setTimeout(() => {
              try {
                const pendingDataStr = sessionStorage.getItem(pendingKey);
                if (pendingDataStr) {
                  const pendingData = JSON.parse(pendingDataStr);
                  
                  // If component didn't show notification, service shows it
                  if (!pendingData.notificationShown) {
                    const notifFileName = fileName || `File ${fileId}`;
                    const notifDocName = documentName || documentModel;
                    const finalOverrideCount = pendingData.overriddenCount || 0;
                    const finalFieldCount = pendingData.autoFilledCount || actualFieldCount;
                    
                    if (finalFieldCount > 0) {
                      this.notificationService.showAutoFillSuccess(
                        notifDocName,
                        notifFileName,
                        finalFieldCount,
                        finalOverrideCount > 0 ? finalOverrideCount : undefined
                      );
                    } else {
                      this.notificationService.showAutoFillError(
                        notifDocName,
                        notifFileName,
                        'Autofill not applicable.',
                        'Error',
                        'Extracted values are not available'
                      );
                    }
                    
                    // Mark as shown to prevent duplicate
                    pendingData.notificationShown = true;
                    sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
                  } else {
                  }
                }
              } catch (err) {
              }
            }, 2000); // Wait 2 seconds for component

            // Cleanup persisted extraction state for this file
            try {
              const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
              delete extractions[fileId.toString()];
              if (Object.keys(extractions).length > 0) {
                localStorage.setItem('truthManagement_extractions', JSON.stringify(extractions));
              } else {
                localStorage.removeItem('truthManagement_extractions');
              }
            } catch (_) {}

            // Remove polling subscription
            this.pollingSubscriptions.delete(requestId);
            
            // Notify components that extraction completed
            this.extractionCompletedSubject.next({fileId, success: actualFieldCount > 0});
            if (onComplete) { onComplete(); }
          },
          error: (err) => {
            try {
              const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
              delete extractions[fileId.toString()];
              if (Object.keys(extractions).length > 0) {
                localStorage.setItem('truthManagement_extractions', JSON.stringify(extractions));
              } else {
                localStorage.removeItem('truthManagement_extractions');
              }
            } catch (_) {}
            this.pollingSubscriptions.delete(requestId);
            this.notificationService.showAutoFillError(
              documentName || documentModel,
              fileName || `File ${fileId}`,
              'Autofill Failed.',
              'Reason',
              'Technical reason'
            );
            
            // Notify components that extraction failed
            this.extractionCompletedSubject.next({fileId, success: false});
          }
        });

        // Track subscription so it can be cancelled if needed
        this.pollingSubscriptions.set(requestId, pollingSub);
      },
      error: (err) => {
        this.notificationService.showAutoFillError(
          documentName || documentModel,
          fileName || `File ${fileId}`,
          'Autofill Failed.',
          'Reason',
          'Technical error'
        );
        // Notify components that extraction failed so spinner clears
        this.extractionCompletedSubject.next({fileId, success: false});
      }
    });
  }

  /**
   * Resume polling for an existing requestId (used when returning to the page after navigation/refresh)
   */
  resumePollingForRequest(requestId: string, fileId: number | string, documentModel?: string, fileName?: string): void {
    if (!requestId) {
      return;
    }
    if (this.pollingSubscriptions.has(requestId)) {
      return;
    }
    const pollingSub = interval(this.POLLING_INTERVAL).pipe(
      startWith(0),
      switchMap(() => this.pollExtractionStatus(requestId)),
      takeWhile((resp: any) => !!resp && resp.status !== 'COMPLETED' && resp.status !== 'FAILED', true),
      filter((resp: any) => !!resp && (resp.status === 'COMPLETED' || resp.status === 'FAILED')),
      take(1)
    ).subscribe({
      next: (completedResp: any) => {
        // Handle FAILED status — blank/flowchart PDFs
        if (completedResp.status === 'FAILED') {
          const notifFileName = fileName || `File ${fileId}`;
          const notifDocName = documentModel || 'Document';
          this.notificationService.showAutoFillError(
            notifDocName,
            notifFileName,
            'Autofill not applicable.',
            'Error',
            'Extracted values are not available'
          );
          // Cleanup persisted extraction state
          try {
            const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
            delete extractions[fileId.toString()];
            if (Object.keys(extractions).length > 0) {
              localStorage.setItem('truthManagement_extractions', JSON.stringify(extractions));
            } else {
              localStorage.removeItem('truthManagement_extractions');
            }
          } catch (_) {}
          this.pollingSubscriptions.delete(requestId);
          this.extractionCompletedSubject.next({fileId, success: false});
          return;
        }

        const attributes = completedResp.attributes || {};
        
        // Restore isOverrideMode from localStorage extraction state
        let isOverrideMode = false;
        try {
          const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
          const extractionState = extractions[fileId.toString()];
          if (extractionState && extractionState.isOverrideMode !== undefined) {
            isOverrideMode = extractionState.isOverrideMode;
          }
        } catch (err) {
        }
        
        const pendingKey = `truthManagement_pendingResults_${fileId}`;
        const pendingData = { fileId, extractedAttrs: attributes, timestamp: Date.now(), isOverrideMode: isOverrideMode };
        try {
          sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
        } catch (err) {
        }

        // Count fields and calculate override count if in override mode
        let actualFieldCount = 0;
        let overriddenCount = 0;
        
        if (isOverrideMode) {
          // Try to read existing values from sessionStorage to calculate override count
          try {
            const modifiedValuesKey = `truthManagement_modifiedValues_${fileId}`;
            const truthValuesKey = `truthManagement_truthValues_${fileId}`;
            const existingModifiedStr = sessionStorage.getItem(modifiedValuesKey);
            const existingTruthStr = sessionStorage.getItem(truthValuesKey);
            const existingModified = existingModifiedStr ? JSON.parse(existingModifiedStr) : {};
            const existingTruth = existingTruthStr ? JSON.parse(existingTruthStr) : {};
            
            for (const [key, value] of Object.entries(attributes)) {
              let extractedValue = '';
              if (Array.isArray(value) && value.length > 0) {
                const firstItem = value[0];
                extractedValue = firstItem?.Value || firstItem?.value || '';
              } else if (value && typeof value === 'object') {
                extractedValue = (value as any).Value || (value as any).value || '';
              }
              
              if (extractedValue && extractedValue.toString().trim()) {
                actualFieldCount++;
                
                // Check if this field had existing value
                const hasExistingModified = existingModified[key] && existingModified[key].trim && existingModified[key].trim();
                const hasExistingTruth = existingTruth[key] && existingTruth[key].value && existingTruth[key].value.trim && existingTruth[key].value.trim();
                
                if (hasExistingModified || hasExistingTruth) {
                  overriddenCount++;
                }
              }
            }
          } catch (err) {
          }
        } else {
          // Normal mode - just count fields
          for (const [key, value] of Object.entries(attributes)) {
            if (Array.isArray(value) && value.length > 0) {
              const firstItem = value[0];
              const extractedValue = firstItem?.Value || firstItem?.value || '';
              if (extractedValue && extractedValue.toString().trim()) {
                actualFieldCount++;
              }
            } else if (value && typeof value === 'object') {
              const extractedValue = (value as any).Value || (value as any).value || '';
              if (extractedValue && extractedValue.toString().trim()) {
                actualFieldCount++;
              }
            }
          }
        }

        // Immediately show error notification if no values were extracted
        if (actualFieldCount === 0) {
          const notifFileName = fileName || `File ${fileId}`;
          const notifDocName = documentModel || 'Document';
          this.notificationService.showAutoFillError(
            notifDocName,
            notifFileName,
            'Autofill not applicable.',
            'Error',
            'Extracted values are not available'
          );
        }

        // Fallback: If component doesn't show notification (user navigated away), service shows it
        // Wait 2 seconds for component to show notification, then check if it was shown
        setTimeout(() => {
          try {
            const pendingDataStr = sessionStorage.getItem(pendingKey);
            if (pendingDataStr) {
              const pendingData = JSON.parse(pendingDataStr);
              
              // If component didn't show notification, service shows it
              if (!pendingData.notificationShown) {
                const notifFileName = fileName || `File ${fileId}`;
                const notifDocName = documentModel || 'Document';
                const finalOverrideCount = pendingData.overriddenCount || 0;
                const finalFieldCount = pendingData.autoFilledCount || actualFieldCount;
                
                if (finalFieldCount > 0) {
                  this.notificationService.showAutoFillSuccess(
                    notifDocName,
                    notifFileName,
                    finalFieldCount,
                    finalOverrideCount > 0 ? finalOverrideCount : undefined
                  );
                } else {
                  this.notificationService.showAutoFillError(
                    notifDocName,
                    notifFileName,
                    'Autofill not applicable.',
                    'Error',
                    'Extracted values are not available'
                  );
                }
                
                // Mark as shown to prevent duplicate
                pendingData.notificationShown = true;
                sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
              } else {
              }
            }
          } catch (err) {
          }
        }, 2000); // Wait 2 seconds for component

        // Clean up persisted extraction state for this file
        try {
          const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
          delete extractions[fileId.toString()];
          if (Object.keys(extractions).length > 0) {
            localStorage.setItem('truthManagement_extractions', JSON.stringify(extractions));
          } else {
            localStorage.removeItem('truthManagement_extractions');
          }
        } catch (_) {}
        this.pollingSubscriptions.delete(requestId);
        // Notify components that extraction completed
        this.extractionCompletedSubject.next({fileId, success: actualFieldCount > 0});
      },
      error: (err) => {
        try {
          const extractions = JSON.parse(localStorage.getItem('truthManagement_extractions') || '{}');
          delete extractions[fileId.toString()];
          if (Object.keys(extractions).length > 0) {
            localStorage.setItem('truthManagement_extractions', JSON.stringify(extractions));
          } else {
            localStorage.removeItem('truthManagement_extractions');
          }
        } catch (_) {}
        this.pollingSubscriptions.delete(requestId);
        this.notificationService.showAutoFillError(
          documentModel || 'Document',
          fileName || `File ${fileId}`,
          'Autofill Failed.',
          'Reason',
          'Technical error'
        );
        
        // Notify components that extraction failed
        this.extractionCompletedSubject.next({fileId, success: false});
      }
    });

    this.pollingSubscriptions.set(requestId, pollingSub);
  }

  /**
   * Get consolidated dashboard data for all document models
   * GET /api/v1/eval/dashboard
   * Returns: document models with keys, latest run, eval files, and summary stats
   */
  getDashboard(): Observable<DashboardResponse> {
    // Return cached data if available
    const cached = this.dashboardCacheSubject.getValue();
    if (cached && cached.document_models?.length > 0) {
      return of(cached);
    }
    
    // Prevent concurrent requests
    if (this.dashboardLoading) {
      return this.dashboardCache$.pipe(
        filter((data): data is DashboardResponse => data !== null && data.document_models?.length > 0),
        take(1)
      );
    }
    const url = `${this.apiUrlV1}/eval/dashboard`;
    this.dashboardLoading = true;
    
    return this.http.get<DashboardResponse>(url).pipe(
      map(response => {
        // Cache the response
        this.setDashboardCache(response);
        this.dashboardLoading = false;
        return response;
      }),
      catchError(error => {
        this.dashboardLoading = false;
        throw error;
      })
    );
  }

  /**
   * Get evaluation summary data for all document models
   * GET /api/v1/eval/summary
   * @param clientId - Optional client ID to filter by specific client
   */
  getEvalSummary(clientId?: string): Observable<EvalSummaryResponse> {
    const cacheKey = clientId || 'default';
    
    // Return cached result if available
    if (this.evalSummaryCache.has(cacheKey)) {
      return this.evalSummaryCache.get(cacheKey)!;
    }

    const url = `${this.apiUrlV1}/eval/summary`;
    let params = new HttpParams();
    
    if (clientId) {
      params = params.set('client_id', clientId);
    } else {
    }
    
    // Make HTTP request with caching
    const request$ = this.http.get<EvalSummaryResponse>(url, { params: params.keys().length > 0 ? params : undefined }).pipe(
      map(response => {
        if (!clientId) {
          this.evalSummaryCacheSubject.next(response);
        }
        return response;
      }),
      catchError(error => {
        throw error;
      }),
      shareReplay(1) // Cache the result for concurrent subscribers
    );
    
    // Store in cache
    this.evalSummaryCache.set(cacheKey, request$);
    return request$;
  }

  /**
   * Force refresh summary data from API
   * @param clientId - Optional client ID to filter by specific client
   */
  refreshEvalSummary(clientId?: string): Observable<EvalSummaryResponse> {
    // Evict the memoized observable so getEvalSummary() issues a fresh HTTP call
    // instead of replaying stale cached data (cache is otherwise never invalidated).
    this.evalSummaryCache.delete(clientId || 'default');
    return this.getEvalSummary(clientId);
  }
  /**
   * Get cached eval summary data synchronously (returns null if not cached).
   */
  getCachedEvalSummary(): EvalSummaryResponse | null {
    return null;
  }

  /**
   * Get extraction summary by document model
   * GET /api/v1/data/extraction-report
   * Results are cached per-client and per-date-range to support client switching.
   * @param clientId - Optional client ID to filter by specific client
   */
  getExtractionReport(startDate: string, endDate?: string, clientId?: string): Observable<ExtractionReportResponse> {
    // Build cache key: includes clientId and date range for accurate cache hits
    const cacheKey = `${clientId || 'default'}|${startDate}|${endDate || 'default'}`;
    
    // Return cached result if available
    if (this.extractionReportCache.has(cacheKey)) {
      return this.extractionReportCache.get(cacheKey)!;
    }

    const url = `${this.apiUrlV1}/data/extraction-report`;
    let params = new HttpParams().set('start_date', startDate);

    if (endDate) {
      params = params.set('end_date', endDate);
    }
    if (clientId) {
      params = params.set('client_id', clientId);
    }
    
    // Make HTTP request with caching
    const request$ = this.http.get<ExtractionReportResponse>(url, { params }).pipe(
      shareReplay(1) // Cache the result for concurrent subscribers
    );
    
    // Store in cache
    this.extractionReportCache.set(cacheKey, request$);
    return request$;
  }

  /**
   * Get list of available clients
   * GET /api/v1/admin/clients
   */
  getClients(): Observable<Client[]> {
    const url = `${this.apiUrlV1}/admin/clients`;
    return this.http.get<Array<{client_id: string; client_name: string}>>(url).pipe(
      map(clients => clients.map(client => ({
        client_id: client.client_id,
        client_name: client.client_name,
        is_active: true
      })))
    );
  }

  /**
   * Get extraction request details with server-side filtering/sorting/pagination.
   * GET /api/v1/data/extraction_request_details
   */
  getExtractionRequestDetails(query: ExtractionRequestDetailsQuery): Observable<ExtractionRequestDetailsResponse> {
    const url = `${this.apiUrlV1}/data/extraction_request_details`;

    let params = new HttpParams()
      .set('document_model', query.document_model)
      .set('start_date', query.start_date)
      .set('end_date', query.end_date);

    if (query.search !== undefined) {
      params = params.set('search', query.search);
    }

    if (query.sort_by !== undefined) {
      params = params.set('sort_by', query.sort_by);
    }

    if (query.sort_dir !== undefined) {
      params = params.set('sort_dir', query.sort_dir);
    }

    if (query.offset !== undefined) {
      params = params.set('offset', String(query.offset));
    }

    if (query.limit !== undefined) {
      params = params.set('limit', String(query.limit));
    }

    return this.http.get<ExtractionRequestDetailsResponse>(url, { params });
  }

  /**
   * Get sequencing request details with server-side filtering/sorting/pagination.
   * GET /api/v1/sequencing/request_details
   */
  getSequencingRequestDetails(query: SequencingRequestDetailsQuery): Observable<SequencingRequestDetailsResponse> {
    const url = `${this.apiUrlV1}/sequencing/request_details`;

    let params = new HttpParams()
      .set('client_id', query.client_id)
      .set('start_date', query.start_date)
      .set('end_date', query.end_date);

    if (query.sort_by !== undefined) {
      params = params.set('sort_by', query.sort_by);
    }

    if (query.sort_dir !== undefined) {
      params = params.set('sort_dir', query.sort_dir);
    }

    if (query.offset !== undefined) {
      params = params.set('offset', String(query.offset));
    }

    if (query.limit !== undefined) {
      params = params.set('limit', String(query.limit));
    }

    return this.http.get<SequencingRequestDetailsResponse>(url, { params });
  }

  /**
   * Get sequencing poll data for a specific request.
   * GET /api/v1/sequencing/poll_sequence?request_id={id}
   */
  getSequencingPollSequence(query: SequencingPollSequenceQuery): Observable<SequencingPollSequenceResponse> {
    const url = `${this.apiUrlV1}/sequencing/poll_sequence`;
    const params = new HttpParams().set('request_id', query.request_id);
    return this.http.get<SequencingPollSequenceResponse>(url, { params });
  }

  /**
   * Download sequencing execution data.
   * GET /api/v1/sequencing/execution_data?request_id={id}&client_request_id={id}&data_type=pdfs
   */
  getSequencingExecutionData(query: SequencingExecutionDataQuery): Observable<Blob> {
    const url = `${this.apiUrlV1}/sequencing/execution_data`;
    const params = new HttpParams()
      .set('request_id', query.request_id)
      .set('client_request_id', query.client_request_id)
      .set('data_type', query.data_type);

    return this.http.get(url, { params, responseType: 'blob' });
  }

  /**
   * Get document types filtered by module for sequencing flows.
   * GET /api/v1/sequencing/document_types?module={module}
   */
  getSequencingDocumentTypes(query: SequencingDocumentTypesQuery): Observable<SequencingDocumentTypeItem[]> {
    const url = `${this.apiUrlV1}/sequencing/document_types`;
    const params = new HttpParams().set('module', query.module);
    return this.http.get<SequencingDocumentTypeItem[]>(url, { params });
  }

  /**
   * Get a signed URL + byte length + per-page dimensions for the tune source PDF.
   * GET /tune/{request_id}/pdf-url  (note: no /api/v1 prefix — matches the pattern
   * of saveTuneAnnotations below)
   * Response drives the Source view's custom PDF.js pipeline (BlobRangeTransport
   * + placeholder layout + IntersectionObserver render).
   */
  getTuneSourcePdfUrl(requestId: string): Observable<TuneSourcePdfUrlResponse> {
    const url = `${environment.apiBaseUrl}/tune/${encodeURIComponent(requestId)}/pdf-url`;
    return this.http.get<TuneSourcePdfUrlResponse>(url);
  }

  /**
   * Save annotation edits for a sequencing request.
   * POST /tune/{request_id}/save
   */
  saveTuneAnnotations(requestId: string, payload: TuneSaveAnnotationsRequest): Observable<TuneSaveAnnotationsResponse> {
    const url = `${environment.apiBaseUrl}/tune/${requestId}/save`;
    return this.http.post<TuneSaveAnnotationsResponse>(url, payload);
  }

  /**
   * STUB — real endpoint TBD. Save a user-drawn bounding box for a source attribute.
   * Returns a 300ms delayed success so the UI can show a saving spinner.
   * When the real endpoint ships, swap the stub body for the http.post call below.
   */
  saveSourceAttributeBoundingBox(
    requestId: string,
    payload: {
      file_id: string;
      attribute_id: number;
      page_number: number;
      page_width: number;
      bounding_box: [number, number, number, number];
    },
  ): Observable<{ status: 'ok' | 'failed'; message?: string }> {
    return of({ status: 'ok' as const }).pipe(delay(300));

    // Real implementation when the endpoint ships:
    // const url = `${environment.apiBaseUrl}/tune/${encodeURIComponent(requestId)}/attributes/${payload.attribute_id}/bbox`;
    // return this.http.post<{ status: 'ok' | 'failed'; message?: string }>(url, payload);
  }

  /**
   * STUB — real endpoint TBD. Revert a user-drawn bounding box back to the auto-detected original.
   */
  revertSourceAttributeBoundingBox(
    requestId: string,
    payload: { file_id: string; attribute_id: number },
  ): Observable<{ status: 'ok' | 'failed'; message?: string }> {
    return of({ status: 'ok' as const }).pipe(delay(300));

    // Real implementation when the endpoint ships:
    // const url = `${environment.apiBaseUrl}/tune/${encodeURIComponent(requestId)}/attributes/${payload.attribute_id}/bbox`;
    // return this.http.delete<{ status: 'ok' | 'failed'; message?: string }>(url);
  }
  
  /**
   * Force refresh dashboard data from API
   * Call this when data has changed (e.g., after evaluation completes)
   */
  refreshDashboard(): Observable<DashboardResponse> {
    this.setDashboardCache(null);
    return this.getDashboard();
  }
  
  /**
   * Get cached dashboard data synchronously (returns null if not cached)
   */
  getCachedDashboard(): DashboardResponse | null {
    return this.dashboardCacheSubject.getValue();
  }

  // ============================================
  // STATE MANAGEMENT METHODS (Unified Service)
  // ============================================

  /**
   * Build compound Map key so both run types can coexist per document model.
   * Key format: "docModel:all" or "docModel:part"
   */
  private makeEvalKey(documentModel: string, runType: 'all' | 'part'): string {
    return `${documentModel}:${runType}`;
  }

  /** Synchronous snapshot of the current timers map (for backup-timer guards in components). */
  getTimersSnapshot(): Map<string, string> {
    return this.timersSubject.getValue();
  }

  /**
   * Get current active evaluations as a snapshot (compound-keyed Map)
   */
  getActiveEvaluations(): Map<string, ActiveEvaluation> {
    return new Map(this.activeEvaluationsSubject.getValue());
  }

  /**
   * Get active evaluation � backward compat: prefers full eval, falls back to test run.
   */
  getActiveEvaluation(documentModel: string): ActiveEvaluation | undefined {
    const current = this.activeEvaluationsSubject.getValue();
    return current.get(this.makeEvalKey(documentModel, 'all'))
        || current.get(this.makeEvalKey(documentModel, 'part'));
  }

  /** Get the active FULL evaluation (run_type=all) for a document model, if any. */
  getActiveFullEvaluation(documentModel: string): ActiveEvaluation | undefined {
    return this.activeEvaluationsSubject.getValue().get(this.makeEvalKey(documentModel, 'all'));
  }

  /** Get the active TEST evaluation (run_type=part) for a document model, if any. */
  getActiveTestEvaluation(documentModel: string): ActiveEvaluation | undefined {
    return this.activeEvaluationsSubject.getValue().get(this.makeEvalKey(documentModel, 'part'));
  }

  /** Find an active evaluation by its evaluation_id across all documents and run types. */
  getActiveEvaluationById(evaluationId: string): ActiveEvaluation | undefined {
    for (const eval_ of this.activeEvaluationsSubject.getValue().values()) {
      if (eval_.evaluation_id === evaluationId) return eval_;
    }
    return undefined;
  }

  /**
   * Add or update an active evaluation.
   * Uses compound key "documentModel:runType" so full and test runs coexist.
   */
  setActiveEvaluation(documentModel: string, evaluation: ActiveEvaluation): void {
    // Block re-creation of just-completed evaluations (prevents glitch restart)
    if (this.completedEvaluationIds.has(evaluation.evaluation_id)) {
      return;
    }

    const runType = evaluation.run_type || 'all';
    const key = this.makeEvalKey(documentModel, runType);
    const current = this.activeEvaluationsSubject.getValue();

    // Preserve the existing started_at (client display anchor) if the incoming object
    // doesn't have one, OR if a timer is already running for this evaluation.
    // This prevents page-load data fetches and navigation events from overwriting the
    // Date.now() anchor that updateEvaluationStatus stored for the live timer.
    const existing = current.get(key);
    if (existing?.started_at && (this.timerIntervals.has(evaluation.evaluation_id) || !evaluation.started_at)) {
      evaluation.started_at = existing.started_at;
    }

    current.set(key, evaluation);
    this.activeEvaluationsSubject.next(new Map(current));

    // Start timer if not already running (handles page refresh/navigation)
    if (evaluation.started_at && !this.timerIntervals.has(evaluation.evaluation_id)) {
      this.startTimer(evaluation.evaluation_id, evaluation.started_at);
    }

    this.persistActiveEvaluations();
  }

  /** Re-emit the current active evaluations so all subscribers re-process with latest documentModel. */
  refreshActiveEvaluations(): void {
    this.activeEvaluationsSubject.next(new Map(this.activeEvaluationsSubject.getValue()));
  }

  /**
   * Update active evaluation status from polling.
   * runType is captured at poll-start so the correct compound key is always used.
   */
  updateEvaluationStatus(documentModel: string, status: EvaluationStatus, runType: 'all' | 'part' = 'all'): void {
    const current = this.activeEvaluationsSubject.getValue();
    const key = this.makeEvalKey(documentModel, runType);
    const evaluation = current.get(key);
    if (evaluation) {
      evaluation.status = status;

      // First poll (any non-terminal status):
      //   - Store server's started_at so page refresh/navigation resumes at real elapsed time
      //   - Anchor display timer to Date.now() so UI always starts from 0s on first poll
      // timerIntervals guard ensures subsequent polls never restart the running timer.
      if (status.status !== 'COMPLETED' && status.status !== 'FAILED' && !this.timerIntervals.has(evaluation.evaluation_id)) {
        if (status.started_at) {
          evaluation.started_at = status.started_at; // persist server timestamp for refresh resume
        }
        const displayAnchor = new Date().toISOString(); // UI counts from 0s at this moment
        this.startTimer(evaluation.evaluation_id, displayAnchor);
      } else if (!this.timerIntervals.has(evaluation.evaluation_id) && evaluation.started_at) {
        // Refresh/navigation: resume from stored server timestamp (shows real elapsed)
        this.startTimer(evaluation.evaluation_id, evaluation.started_at);
      }

      this.activeEvaluationsSubject.next(new Map(current));
      this.persistActiveEvaluations();
    }
  }
  /**
   * Remove BOTH run types for a document model (backward-compat / error cleanup path).
   */
  clearActiveEvaluation(documentModel: string): void {
    const current = this.activeEvaluationsSubject.getValue();
    current.delete(this.makeEvalKey(documentModel, 'all'));
    current.delete(this.makeEvalKey(documentModel, 'part'));
    this.activeEvaluationsSubject.next(new Map(current));
    this.persistActiveEvaluations();
  }

  /**
   * Remove only the specified run type for a document model.
   * Used by polling completion so a concurrent run of the other type is NOT cleared.
   */
  clearActiveRun(documentModel: string, runType: 'all' | 'part'): void {
    const current = this.activeEvaluationsSubject.getValue();
    current.delete(this.makeEvalKey(documentModel, runType));
    this.activeEvaluationsSubject.next(new Map(current));
    this.persistActiveEvaluations();
  }
  /**
   * Check if a new run can be started for a document model
   * SIMPLIFIED APPROACH: Only ONE run per document model at any time
   * Blocks ANY new run if ANY run is currently active (test OR full)
   * 
   * ACTIVE STATUS DEFINITION:
   * - Active = Any status EXCEPT 'COMPLETED' or 'FAILED'
   * - This includes: PENDING, RUNNING, and any other intermediate states
   */
  /**
   * Pre-flight check before starting a run
   * Calls /eval/runs API to verify no active runs exist for this document model
   * ON-DEMAND API CHECK - called once before each run attempt
   */
  checkForActiveRuns(documentModel: string, runType: 'all' | 'part'): Observable<{
    canStart: boolean;
    reason?: string;
    activeRun?: any;
  }> {
    // Only check for the SAME run type being requested.
    // This allows a full eval to start while a test run is in progress, and vice versa.
    return this.getEvaluationRuns(documentModel, runType).pipe(
      map(runsResponse => {
        const activeRun = runsResponse.runs?.find(
          (r: any) => r.status === 'PENDING' || r.status === 'RUNNING'
        );

        if (!activeRun) {
          return { canStart: true };
        }

        const requestedTypeName = runType === 'all' ? 'full evaluation' : 'test run';
        const startedAtStr = activeRun.started_at?.endsWith?.('Z')
          ? activeRun.started_at
          : (activeRun.started_at || '') + 'Z';
        const startedAtDisplay = new Date(startedAtStr).toLocaleString();

        return {
          canStart: false,
          reason: `Cannot start ${requestedTypeName}. A ${requestedTypeName} is already ${activeRun.status} for ${documentModel}.\n\nStatus: ${activeRun.status}\nStarted: ${startedAtDisplay}\n\nPlease wait for it to complete or check the Accuracy page.`,
          activeRun: activeRun
        };
      }),
      catchError(error => {
        return of({ canStart: true });
      })
    );
  }
  
  /**
   * Start polling for evaluation status
   */
  startStatusPolling(evaluationId: string, documentModel: string): void {
    // Reset error counter for this evaluation
    this.errorCounts.set(evaluationId, 0);

    const startingEval = this.getActiveEvaluationById(evaluationId);
    const capturedRunType: 'all' | 'part' = startingEval?.run_type || 'all';

    // Cancel any existing polling subscription WITHOUT killing the running timer.
    // The old stopStatusPolling() call here also called stopTimer(), causing visible
    // duration gaps every time startStatusPolling is called on navigation.
    const _existingSub = this.pollingSubscriptions.get(evaluationId);
    if (_existingSub) { _existingSub.unsubscribe(); this.pollingSubscriptions.delete(evaluationId); }
    // Restart timer only if it was lost (first call from active-run check without setActiveEvaluation)
    if (startingEval?.started_at && !this.timerIntervals.has(evaluationId)) {
      this.startTimer(evaluationId, startingEval.started_at);
    }

    let isPolling = true;

    const subscription = interval(this.POLLING_INTERVAL)
      .pipe(
        startWith(0),         // fire immediately on subscription, then every 5s
        takeWhile(() => isPolling),
        switchMap(() =>
          this.getEvaluationStatus(evaluationId).pipe(
            catchError((error) => {
              const currentErrors = (this.errorCounts.get(evaluationId) || 0) + 1;
              this.errorCounts.set(evaluationId, currentErrors);

              if (currentErrors >= this.MAX_CONSECUTIVE_ERRORS) {
                isPolling = false;
                return of({ _stopPolling: true } as any);
              }

              return of(null);
            })
          )
        )
      )
      .subscribe({
        next: (status: EvaluationStatus | null | any) => {
          if (status && status._stopPolling) {
            this.stopStatusPolling(evaluationId);
            this.clearActiveRun(documentModel, capturedRunType);
            return;
          }

          if (!status) return;

          // Success � reset error counter
          this.errorCounts.set(evaluationId, 0);

          // Update the evaluation status using the compound key for this run type
          this.updateEvaluationStatus(documentModel, status, capturedRunType);

          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            // Snapshot the live client timer — this is exactly what the user sees on screen.
            // Server timestamps are NOT used here: the client anchor is Date.now() at first
            // RUNNING poll, which is later than server's started_at. Using server math would
            // produce a higher number (e.g. 1m3s vs 1m0s) causing a visible jump on freeze.
            const _frozenVal = this.timersSubject.getValue().get(evaluationId) || '';
            this.stopTimer(evaluationId);

            this.emitCompletionEvent({
              evaluationId: evaluationId,
              documentModel: documentModel,
              status: status.status,
              runType: capturedRunType,
              failedFiles: status.failed_files || 0
            });

            isPolling = false;
            this.stopStatusPolling(evaluationId);
            // Re-insert frozen value so active card shows correct final duration until hidden
            if (_frozenVal) { const _t = this.timersSubject.getValue(); _t.set(evaluationId, _frozenVal); this.timersSubject.next(new Map(_t)); }
            // Clear only THIS run type — preserves a concurrent run of the other type
            this.clearActiveRun(documentModel, capturedRunType);
          }
        },
        error: () => {
          isPolling = false;
          this.stopStatusPolling(evaluationId);
          this.clearActiveRun(documentModel, capturedRunType);
        }
      });

    this.pollingSubscriptions.set(evaluationId, subscription);
  }
  /**
   * Stop polling for evaluation status
   */
  stopStatusPolling(evaluationId: string): void {
    const subscription = this.pollingSubscriptions.get(evaluationId);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(evaluationId);
    }
    // Stop timer for this evaluation
    this.stopTimer(evaluationId);
    // Clear error counter
    this.errorCounts.delete(evaluationId);
  }

  /**
   * Clear all active evaluations and stop all polling
   */
  clearAll(): void {
    this.pollingSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.pollingSubscriptions.clear();
    this.errorCounts.clear();
    this.activeEvaluationsSubject.next(new Map());
    
    // Clear all timers
    this.timerIntervals.forEach((interval) => clearInterval(interval));
    this.timerIntervals.clear();
    this.timersSubject.next(new Map());
    
    // Clear pending requests
    this.pendingRunRequests.clear();
    
    // Clear completion tracking
    this.completedEvaluationIds.clear();

    // Clear sessionStorage
    try { sessionStorage.removeItem('activeEvaluations'); } catch { /* ignore */ }
  }
  
  /**
   * Persist active evaluations to sessionStorage so timer survives page refresh
   */
  private persistActiveEvaluations(): void {
    try {
      const current = this.activeEvaluationsSubject.getValue();
      const serializable = Array.from(current.entries()).map(([key, eval_]) => ({
        key,
        evaluation_id: eval_.evaluation_id,
        job_id: eval_.job_id,
        document_model: eval_.document_model,
        file_count: eval_.file_count,
        started_at: eval_.started_at,
        run_type: eval_.run_type,
        status_status: eval_.status?.status || null,
        status_counts: eval_.status ? {
          status: eval_.status.status,
          total_files: eval_.status.total_files,
          completed_files: eval_.status.completed_files,
          failed_files: eval_.status.failed_files,
          running_files: eval_.status.running_files,
          pending_files: eval_.status.pending_files,
          started_at: eval_.status.started_at,
          completed_at: eval_.status.completed_at
        } : null
      }));
      sessionStorage.setItem('activeEvaluations', JSON.stringify(serializable));
    } catch { /* sessionStorage full or unavailable */ }
  }

  /**
   * Restore active evaluations from sessionStorage on service init (page refresh)
   */
  private restoreActiveEvaluations(): void {
    try {
      const stored = sessionStorage.getItem('activeEvaluations');
      if (!stored) return;
      const entries: any[] = JSON.parse(stored);
      if (!Array.isArray(entries) || entries.length === 0) return;
      const map = new Map<string, ActiveEvaluation>();
      for (const entry of entries) {
        if (entry.status_status === 'COMPLETED' || entry.status_status === 'FAILED') continue;
        const activeEval: ActiveEvaluation = {
          evaluation_id: entry.evaluation_id,
          job_id: entry.job_id,
          document_model: entry.document_model,
          file_count: entry.file_count,
          started_at: entry.started_at,
          status: entry.status_counts || (entry.status_status ? { status: entry.status_status } as any : null),
          run_type: entry.run_type
        };
        map.set(entry.key, activeEval);
        if (activeEval.started_at) {
          this.startTimer(activeEval.evaluation_id, activeEval.started_at);
        }
        this.startStatusPolling(activeEval.evaluation_id, activeEval.document_model);
      }
      if (map.size > 0) {
        this.activeEvaluationsSubject.next(map);
      }
    } catch {
      try { sessionStorage.removeItem('activeEvaluations'); } catch { /* ignore */ }
    }
  }

  /**
   * Emit completion event with deduplication
   * Prevents infinite popup loops when page refreshes or multiple subscriptions exist
   */
  /**
   * Invalidate the runs cache for a document model / run type so the next loadBothRunTypes()
   * always fetches fresh data from the API (bypasses the stale-while-revalidate early return).
   */
  invalidateRunsCache(documentModel: string, runType?: 'all' | 'part'): void {
    if (runType) {
      const key = `${documentModel}:${runType}`;
      this.evaluationRunsCache.delete(key);
      this.evaluationRunsCacheTimestamps.delete(key);
    } else {
      for (const key of Array.from(this.evaluationRunsCache.keys())) {
        if (key.startsWith(`${documentModel}:`)) {
          this.evaluationRunsCache.delete(key);
          this.evaluationRunsCacheTimestamps.delete(key);
        }
      }
    }
  }

  private emitCompletionEvent(event: EvaluationCompletionEvent): void {
    // Check if already emitted
    if (this.completedEvaluationIds.has(event.evaluationId)) {
      return;
    }
    
    // Invalidate the runs cache for the completed run type so the next loadBothRunTypes()
    // always makes a fresh API call — both when evaluation-runs is alive and when it is
    // recreated after a tab switch.
    this.invalidateRunsCache(event.documentModel, event.runType);

    // Mark as emitted
    this.completedEvaluationIds.add(event.evaluationId);
    // Show global toaster notification (visible on any page)
    // This is the single source of truth for validation completion notifications
    if (event.status === 'COMPLETED') {
      this.notificationService.showValidationSuccess(event.documentModel, event.runType, event.failedFiles || 0);
    } else if (event.status === 'FAILED') {
      this.notificationService.showValidationFailed(event.documentModel, event.runType);
    }
    
    // Emit event for components to handle their own concerns (spinners, data refresh)
    this.completionSubject.next(event);
    
    // Invalidate dashboard cache so next page load gets fresh data
    this.setDashboardCache(null);

    // Invalidate eval summary cache too — loan-validation reads via getEvalSummary/refreshEvalSummary,
    // which is a separate cache from the legacy dashboard cache above. Without this, a run started
    // from a page other than loan-validation (e.g. run-validation-details) leaves the cache stale,
    // so the row for this document model still shows pre-run data next time /dashboard loads.
    this.evalSummaryCache.clear();
    
    // Clean up after memory duration to prevent memory leak
    setTimeout(() => {
      this.completedEvaluationIds.delete(event.evaluationId);
    }, this.COMPLETION_MEMORY_DURATION);
  }
  
  /**
   * Start live duration timer for an evaluation
   */
  startTimer(evaluationId: string, startedAt: string): void {
    // Skip if timer already running for this evaluation (prevents flicker/reset)
    if (this.timerIntervals.has(evaluationId)) {
      return;
    }
    
    // Parse start time
    const startTime = new Date(startedAt.endsWith('Z') ? startedAt : startedAt + 'Z').getTime();
    
    const updateTimer = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const formatted = this.formatDuration(elapsed);
      
      const timers = this.timersSubject.getValue();
      timers.set(evaluationId, formatted);
      this.timersSubject.next(new Map(timers));
    };
    
    // Update immediately
    updateTimer();
    
    // Then update every second
    const interval = setInterval(updateTimer, 1000);
    this.timerIntervals.set(evaluationId, interval);
  }
  
  /**
   * Stop timer for an evaluation
   */
  stopTimer(evaluationId: string): void {
    const interval = this.timerIntervals.get(evaluationId);
    if (interval) {
      clearInterval(interval);
      this.timerIntervals.delete(evaluationId);
    }
    
    // Remove from timers map
    const timers = this.timersSubject.getValue();
    timers.delete(evaluationId);
    this.timersSubject.next(new Map(timers));
  }
  
  /**
   * Format seconds into human-readable duration
   */
  private formatDuration(totalSeconds: number): string {
    if (totalSeconds <= 0) return '0s';
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.clearAll();
  }

  // =======================================
  // EVALUATION API METHODS
  // =======================================

  // Evaluation Methods
  runEvaluation(documentModel: string, fileIds?: string[]): Observable<any> {
    const isPartialRun = fileIds && fileIds.length > 0;
    const url = `${this.apiUrlV1}/eval/run`;
    const requestBody: any = {
      document_model: documentModel,
      // run_all: true tells the backend to run all evaluation files for this model.
      // run_all: false is used only for partial runs where specific file_ids are supplied.
      run_all: !isPartialRun,
      file_ids: isPartialRun ? fileIds : []
    };

    if (isPartialRun) {
    } else {
    }
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    
    return this.http.post<any>(url, requestBody, { headers }).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        if (error.error && error.error.detail) {
        }
        throw error;
      })
    );
  }
  
  /**
   * Safe wrapper around runEvaluation with duplicate request prevention
   * and pre-flight validation via API to prevent concurrent runs
   * 
   * This is the RECOMMENDED method to call from components
   */
  runEvaluationSafe(documentModel: string, fileIds?: string[]): Observable<any> {
    const runType = (fileIds && fileIds.length > 0) ? 'part' : 'all';
    const requestKey = `${documentModel}_${runType}`;
    // 1. Check for duplicate in-flight request
    const pending = this.pendingRunRequests.get(requestKey);
    if (pending) {
      return pending;
    }
    
    // 2. Pre-flight check: Call API to verify no active runs
    const request$ = this.checkForActiveRuns(documentModel, runType).pipe(
      switchMap(checkResult => {
        if (!checkResult.canStart) {
          // Create error with activeRun data attached for Browser B to adopt
          const error: any = new Error(checkResult.reason || 'Cannot start run');
          error.activeRun = checkResult.activeRun; // Attach active run details
          error.isPreFlightFailure = true; // Flag to identify pre-flight failures
          return throwError(() => error);
        }
        // 3. Start the evaluation
        return this.runEvaluation(documentModel, fileIds);
      }),
      finalize(() => {
        this.pendingRunRequests.delete(requestKey);
      }),
      shareReplay(1)
    );
    
    // 4. Track request
    this.pendingRunRequests.set(requestKey, request$);
    
    return request$;
  }
  
  getEvaluationStatus(evaluationId: string): Observable<EvaluationStatus> {
    if (this.evaluationStatusCache.has(evaluationId)) {
      return this.evaluationStatusCache.get(evaluationId)!;
    }
    const url = `${this.apiUrlV1}/eval/status/${evaluationId}`;
    const obs$ = this.http.get<EvaluationStatus>(url).pipe(
      map(response => {
        const s = response.status?.toUpperCase();
        if (s !== 'COMPLETED' && s !== 'FAILED') {
          // RUNNING/PENDING: expire after 3 s so polling still gets fresh progress data
          setTimeout(() => this.evaluationStatusCache.delete(evaluationId), 3000);
        }
        // COMPLETED/FAILED: cache entry stays permanently (data is immutable)
        return response;
      }),
      catchError(error => {
        this.evaluationStatusCache.delete(evaluationId);
        throw error;
      }),
      shareReplay(1)
    );
    this.evaluationStatusCache.set(evaluationId, obs$);
    return obs$;
  }

  /** Invalidate a cached eval/status entry — call after submitting a new run
   *  so the next fetch reflects the freshly-started evaluation. */
  invalidateEvaluationStatus(evaluationId: string): void {
    this.evaluationStatusCache.delete(evaluationId);
  }

  /**
   * Get evaluation runs for a document model from API
   * GET /api/v1/eval/runs?document_model={model}&run_type={type}
   * @param documentModel - The document model to get runs for
   * @param runType - Optional filter: 'all' for full runs, 'part' for partial/test runs, undefined for all runs
   */
  getEvaluationRuns(documentModel: string, runType?: 'all' | 'part'): Observable<EvaluationRunsResponse> {
    let url = `${this.apiUrlV1}/eval/runs?document_model=${encodeURIComponent(documentModel)}`;
    
    // Add run_type query parameter if specified
    if (runType) {
      url += `&run_type=${runType}`;
    }
    
    return this.http.get<EvaluationRunsResponse>(url).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        // Return empty response on error
        return of({
          status: 'ERROR',
          document_model: documentModel,
          runs: []
        } as EvaluationRunsResponse);
      })
    );
  }

/**
 * Get active evaluation and test runs across all document models
 * GET /api/v1/eval/active-runs
 * Returns document models with active (RUNNING/PENDING) evaluations
 */
getActiveRuns(): Observable<ActiveRunsResponse> {
  const url = `${this.apiUrlV1}/eval/active-runs`;
  
  return this.http.get<ActiveRunsResponse>(url).pipe(
    map(response => {
      return response;
    }),
    catchError(error => {
      // Return empty response on error
      return of({
        active_eval_runs: [],
        active_test_runs: []
      } as ActiveRunsResponse);
    })
  );
}
  // Loan Methods - returning empty arrays (no mock data)
  getLoans(): Observable<Loan[]> {
    // No mock data - return empty array
    return of([]);
  }
  getLoanById(id: number): Loan | undefined {
    // No mock data available
    return undefined;
  }
  setSelectedLoan(loan: Loan | null): void {
    this.selectedLoanSubject.next(loan);
    if (!loan) {
      this.selectedDocumentSubject.next(null);
    }
  }
  getSelectedLoan(): Observable<Loan | null> {
    return this.selectedLoanSubject.asObservable();
  }
  // ============================================
  // DOCUMENT API METHODS - Connected to SQL Server Database
  // ============================================
  
  /**
   * API 1: Get all documents from database
   * No mock data available - use Swagger API
   */
  getAllDocuments(): Observable<Document[]> {
    // No mock data - return empty array
    return of([]);
  }
  /**
   * Get documents filtered by loan ID
   * No localhost backend available
   */
  getDocumentsByLoanId(loanId: number): Observable<Document[]> {
    // No localhost backend - return empty array
    return of([]);
  }
  /**
   * Get a single document by ID
   * No mock data available
   */
  getDocument(documentId: number): Observable<Document> {
    // No mock data available
    throw new Error(`Document ${documentId} not found - no mock data available`);
  }
  /**
   * API 2: Add new document to database
   */
  addDocument(
    loanId: number,
    documentNumber: string,
    documentName: string,
    description?: string
  ): Observable<Document> {
    // No localhost backend available - functionality disabled
    throw new Error('addDocument: No localhost backend available');
  }
  /**
   * API 3: Delete document from database
   * No localhost backend available
   */
  deleteDocument(documentId: number): Observable<void> {
    throw new Error('deleteDocument: No localhost backend available');
  }
  /**
   * FILE API METHODS
   */
  getAllFiles(documentId?: number): Observable<File[]> {
    // No mock data - use Swagger API getEvaluationFiles() instead
    return of([]);
  }
  getFileContentUrl(fileId: number | string): Observable<any> {
    // No localhost backend available
    return of(null);
  }
  addFile(
    documentId: number,
    fileName: string,
    filePath?: string,
    fileSize?: number,
    mimeType?: string
  ): Observable<File> {
    // No localhost backend - use uploadFile() with Swagger API instead
    throw new Error('addFile: No localhost backend available - use uploadFile()');
  }
  /**
   * Upload file to the evaluation API endpoint (direct FormData approach)
   */
  uploadFile(
    file: globalThis.File,
    documentId: number,
    documentCode: string
  ): Observable<File> {
    // Try direct FormData upload with all required fields
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('document_model', documentCode);
    formData.append('file_name', file.name);
    formData.append('eval_type', 'data_extraction');
    
    // Debug: Log what we're sending
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
      } else {
      }
    }
    return this.http.post<any>(`${this.apiUrlV1}/eval/files`, formData, {
      headers: {
        'accept': 'application/json'
        // Don't set Content-Type - let browser handle multipart/form-data with boundary
        // Authorization header is added by authInterceptor
      }
    }).pipe(
      map((response: any) => {
        // Convert to File format expected by the component
        const convertedFile: File = {
          id: response.id || response.file_id || Date.now().toString(),
          fileName: response.file_name || file.name,
          filePath: response.blob_url || response.file_path || response.blob_sas_url || '',
          documentId: documentId,
          uploadedDate: new Date(response.uploaded_at || response.created_at || new Date().toISOString())
        };
        // Invalidate dashboard cache so any subsequent getDashboard() call fetches fresh data
        this.setDashboardCache(null);
        // Also invalidate the eval summary cache — a new file changes needs_eval_run/files_count
        // for this document model, and loan-validation reads that via getEvalSummary/refreshEvalSummary.
        this.evalSummaryCache.clear();
        return convertedFile;
      }),
      catchError(error => {
        // Even if upload fails, try to show the file locally for preview
        const localFile: File = {
          id: 'temp_' + Date.now(),
          fileName: file.name,
          filePath: 'local_preview',
          documentId: documentId,
          uploadedDate: new Date()
        };
        
        // Don't throw error immediately - let component handle it
        throw error;
      })
    );
  }
  /**
   * Delete an evaluation file (soft delete)
   * DELETE /api/v1/eval/files/{file_id}
   * @param fileId - UUID of the file to delete
   * @returns Observable with success/error response
   */
  deleteFile(fileId: number | string): Observable<{ status: string; message: string }> {
    const url = `${this.apiUrlV1}/eval/files/${fileId}`;
    
    return this.http.delete<{ status: string; message: string }>(url).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Get user role by email with assigned clients
   * GET /api/v1/admin/users/{user_email}
   * @param userEmail - User's email address
   * @returns Observable with user role and clients information
   */
  getUserRole(userEmail: string): Observable<{ status: string; user_email: string; role: string; clients?: Array<{ client_id: string; client_name: string; module?: string }> }> {
    const url = `${this.apiUrlV1}/admin/users/${encodeURIComponent(userEmail)}`;
    return this.http.get<{ status: string; user_email: string; role: string; clients?: Array<{ client_id: string; client_name: string; module?: string }> }>(url).pipe(
      map(response => {
        if (response.clients) {
        }
        return response;
      }),
      catchError(error => {
        // Return default user role on error
        return of({ status: 'ERROR', user_email: userEmail, role: 'user', clients: [] });
      })
    );
  }
  /**
   * ATTRIBUTE API METHODS
   */
  getAllAttributes(documentId?: number): Observable<Attribute[]> {
    // No mock data - return empty array
    return of([]);
  }
  addAttribute(
    attributeKey: string,
    attributeLabel: string,
    description: string,
    documentId: number
  ): Observable<Attribute> {
    // No localhost backend available
    throw new Error('addAttribute: No localhost backend available');
  }
  deleteAttribute(attributeId: number): Observable<void> {
    // No localhost backend available
    throw new Error('deleteAttribute: No localhost backend available');
  }
  updateAttribute(attributeId: number, data: { attributeLabel?: string, description?: string }): Observable<Attribute> {
    // No localhost backend available
    throw new Error('updateAttribute: No localhost backend available');
  }
  addLoan(loanNumber: string, loanName: string): void {
    // No mock data - function disabled
  }
  // Old addDocument method removed - now using API version above
  setSelectedDocument(document: Document | null): void {
    this.selectedDocumentSubject.next(document);
  }
  getSelectedDocument(): Observable<Document | null> {
    return this.selectedDocumentSubject.asObservable();
  }
  // File methods
  getFilesByDocumentId(documentId: number): Observable<File[]> {
    // No mock data - use getEvaluationFiles() for Swagger API
    return of([]);
  }
  // View methods
  setSelectedView(view: 'files' | 'attributes'): void {
    this.selectedViewSubject.next(view);
  }
  getSelectedView(): Observable<'files' | 'attributes'> {
    return this.selectedViewSubject.asObservable();
  }
  // Attribute methods
  // Old mock data methods removed - now using API methods above
  // Truth Values methods - using Swagger API
  getTruthValuesByDocumentId(documentId: number): Observable<TruthValue[]> {
    // No mock data - return empty array
    return of([]);
  }
  getTruthValuesByFileId(fileId: number | string): Observable<AttributesResponse> {
    const url = `${this.apiUrlV2}/eval/files/${fileId}/attributes`;
    // Declare invalidFields outside pipe so it's accessible in both map and catchError
    let invalidFields: string[] = [];
    
    // Store attribute_summary for component access
    return this.http.get<any>(url, {
      headers: { 'accept': 'application/json' }
    }).pipe(
      map(response => {
        // Handle both array and object responses with better detection
        let attributes: any[] = [];
        invalidFields = response?.attribute_summary?.invalid_fields || [];
        if (Array.isArray(response)) {
          attributes = response;
        } else if (response && response.attributes && Array.isArray(response.attributes)) {
          attributes = response.attributes;
        } else if (response && response.data && Array.isArray(response.data)) {
          attributes = response.data;
        } else if (response && response.items && Array.isArray(response.items)) {
          attributes = response.items;
        } else if (response && response.results && Array.isArray(response.results)) {
          attributes = response.results;
        } else if (response && typeof response === 'object') {
          // Try to find any array property
          for (const key of Object.keys(response)) {
            if (Array.isArray(response[key])) {
              attributes = response[key];
              break;
            }
          }
        }
        // Log first attribute structure to debug field mapping
        if (attributes.length > 0) {
        }
        
        // Map API response to TruthValue interface
        const mapped = attributes.map((attr: any, index: number) => {
          const attributeName = attr.attribute || attr.attribute_name || attr.key || attr.key_name || attr.name || '';
          const value = attr.value || attr.attribute_value || attr.truth_value || '';
          
          if (!attributeName) {
          }
          
          return {
            id: attr.id || index + 1,
            attributeId: attr.attribute_id || attr.id || index + 1,
            attributeName: attributeName,
            value: value,
            documentId: 0,
            fileId: fileId,
            createdDate: attr.created_at ? new Date(attr.created_at) : new Date(),
            updatedDate: attr.updated_at ? new Date(attr.updated_at) : new Date(),
            isActive: true,
            // Bounding box data for highlighting (User Story 7126)
            page_number: attr.page_number ?? null,
            bounding_box: attr.bounding_box ?? null,
            data_type: attr.data_type ?? null  // CRITICAL: Required for array field detection
          };
        });
        if (mapped.length > 0) {
        }
        
        // Extract attribute_summary from response
        const attributeSummary = response?.attribute_summary || { total_count: 0, filled_count: 0 ,invalid_fields: invalidFields};
        // Return full AttributesResponse structure
        return {
          file_id: String(fileId),
          file_name: response?.file_name || '',
          document_model: response?.document_model || '',
          attributes: mapped,
          count: mapped.length,
          attribute_summary: attributeSummary
        };
      }),
      catchError(error => {
        if (error.error) {
        }
        // Return empty AttributesResponse on error
        return of({
          file_id: String(fileId),
          file_name: '',
          document_model: '',
          attributes: [],
          count: 0,
          attribute_summary: { total_count: 0, filled_count: 0 , invalid_fields: invalidFields}
        });
      })
    );
  }
  addTruthValue(truthValue: Omit<TruthValue, 'id' | 'createdDate' | 'updatedDate' | 'isActive'> & { attributeName?: string }): Observable<TruthValue> {
    const fileId = truthValue.fileId;
    const requestBody = {
      attribute: truthValue.attributeName || `attribute_${truthValue.attributeId}`,
      value: truthValue.value
    };
    return this.http.post<any>(`${this.apiUrlV1}/eval/files/${fileId}/attributes`, requestBody, {
      headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }
    }).pipe(
      map(response => {
        return {
          id: response.id || 0,
          attributeId: truthValue.attributeId,
          value: response.value || truthValue.value,
          documentId: truthValue.documentId,
          fileId: fileId,
          createdDate: response.created_at ? new Date(response.created_at) : new Date(),
          updatedDate: response.updated_at ? new Date(response.updated_at) : new Date(),
          isActive: true
        };
      }),
      catchError(error => {
        throw error;
      })
    );
  }
  updateTruthValue(id: number | string, updates: { value?: string; updatedBy?: string; fileId?: number | string }): Observable<TruthValue> {
    if (!updates.fileId) {
      throw new Error('fileId is required');
    }
    const fileId = updates.fileId;
    // Use POST with attribute name and value (same as add - API uses upsert behavior)
    const requestBody = { 
      attribute: id, // The attribute name/key
      value: updates.value 
    };
    return this.http.post<any>(`${this.apiUrlV1}/eval/files/${fileId}/attributes`, requestBody, {
      headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }
    }).pipe(
      map(response => {
        return {
          id: typeof id === 'number' ? id : 0,
          attributeId: 0,
          value: response.value || updates.value || '',
          documentId: 0,
          fileId: fileId,
          createdDate: response.created_at ? new Date(response.created_at) : new Date(),
          updatedDate: response.updated_at ? new Date(response.updated_at) : new Date(),
          isActive: true
        };
      }),
      catchError(error => {
        throw error;
      })
    );
  }
  deleteTruthValue(id: number | string, fileId: number | string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrlV1}/eval/files/${fileId}/attributes/${id}`, {
      headers: { 'accept': 'application/json' }
    }).pipe(
      map(() => {
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  // Bulk add/update attributes using the bulk API endpoint
  bulkUpdateAttributes(fileId: number | string, attributes: { 
    attribute: string; 
    feature?: string;          
    data_type?: string; 
    value: string | string[];  // Can be scalar or array (tables are JSON-stringified)
    page_number?: number | number[] | null;  // Single number for non-array, array for array fields
    bounding_box?: number[] | (number[] | null)[] | null;  // Single array for non-array, array of arrays/nulls for array fields
  }[]): Observable<any> {
    // API expects { attributes: [...] } wrapper object
    const payload = { attributes };
    return this.http.post<any>(`${this.apiUrlV2}/eval/files/${fileId}/attributes/bulk`, payload, {
      headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }
    }).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Poll extraction results for a specific request_id
   * Uses /data/poll_extract endpoint to get the actual extracted values
   */
  pollExtractionResults(requestId: string): Observable<any> {
    const url = `${this.apiUrlV1}/data/poll_extract?request_id=${requestId}`;
    
    return this.http.get<any>(url, {
      headers: { 'accept': 'application/json' }
    }).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        return of(null);
      })
    );
  }

  /**
   * Get ground truth from provided evaluation files by matching filename
   * This is used when the component already has loaded evaluation files with attributes
   */
  private getGroundTruthFromEvaluationFiles(fileName: string, evaluationFiles: any[]): TruthValue[] {
    if (!evaluationFiles || evaluationFiles.length === 0) {
      return [];
    }
    
    // Extract just the filename for matching (e.g., "1-4(3).pdf" from "ground_truths/1-4_family_rider/1-4(3).pdf")
    const shortName = fileName.split('/').pop()?.toLowerCase() || fileName.toLowerCase();
    const withoutExt = shortName.replace(/\.[^.]+$/, '').toLowerCase();
    // Try to find a matching file in the evaluation files
    for (const evalFile of evaluationFiles) {
      const evalFileName = (evalFile.file_name || evalFile.fileName || '').toLowerCase();
      const evalShortName = evalFileName.split('/').pop() || evalFileName;
      const evalWithoutExt = evalShortName.replace(/\.[^.]+$/, '');
      
      // Check for matches
      const isMatch = evalFileName === shortName ||
                      evalShortName === shortName ||
                      evalWithoutExt === withoutExt ||
                      evalFileName.includes(shortName) ||
                      shortName.includes(evalShortName);
      
      if (isMatch && evalFile.attributes && evalFile.attributes.length > 0) {
        // Convert attributes to TruthValue format
        const truthValues: TruthValue[] = evalFile.attributes.map((attr: any, index: number) => ({
          id: attr.id || index + 1,
          attributeId: attr.id || index + 1,
          attributeName: attr.attribute || attr.attribute_name || attr.key || '',
          value: attr.value || attr.expected_value || '',
          documentId: 0,
          fileId: evalFile.id || evalFile.file_id || shortName,
          createdDate: new Date(),
          updatedDate: new Date(),
          isActive: true
        }));
        if (truthValues.length > 0) {
        }
        
        return truthValues;
      }
    }
    return [];
  }

  /**
   * Try multiple file_id variations to get ground truth
   * Uses a cascading approach - tries each ID until one succeeds
   */
  private tryMultipleGroundTruthIds(fileIds: string[]): Observable<TruthValue[]> {
    if (fileIds.length === 0) {
      return of([]);
    }
    
    const [first, ...rest] = fileIds;
    return this.getTruthValuesByFileId(encodeURIComponent(first)).pipe(
      switchMap(response => {
        // Extract attributes from AttributesResponse
        const result = response.attributes;
        // If we got results, return them
        if (result && result.length > 0) {
          return of(result);
        }
        // If no results and we have more IDs to try, continue
        if (rest.length > 0) {
          return this.tryMultipleGroundTruthIds(rest);
        }
        // No results and no more IDs
        return of([]);
      }),
      catchError(error => {
        // On error, try the next ID
        if (rest.length > 0) {
          return this.tryMultipleGroundTruthIds(rest);
        }
        return of([]);
      })
    );
  }

  /**
   * Get detailed validation results for a specific evaluation ID
   * 
   * SIMPLIFIED FLOW:
   * 1. Call /eval/status/{evaluation_id} to get complete validation results
   * 2. The response includes results[] array with field_results containing:
   *    - fact: expected/ground truth value
   *    - value: actual/extracted value
   *    - score: similarity score (0-1)
   *    - matched: whether values match
   * 3. Transform results into ValidationDetail[] format for the UI
   * 
   * @param evaluationId - The evaluation run ID
   * @param providedEvaluationFiles - Optional (no longer needed, kept for backwards compatibility)
   */
  getValidationResultsByEvaluationId(evaluationId: string, providedEvaluationFiles?: any[], cachedStatus?: EvaluationStatus): Observable<ValidationDetail[]> {
    if (!evaluationId || evaluationId === 'Not Available') {
      return of([]);
    }

    // When the caller already has the status (e.g. from fetchLatestEvaluationStatus),
    // use it directly to avoid a duplicate HTTP request.
    if (cachedStatus) {
      if (cachedStatus.results && cachedStatus.results.length > 0) {
        return of(this.transformResultsToValidationDetails(evaluationId, cachedStatus.results));
      }
      return of([]);
    }
    
    // Get evaluation status - this now contains everything we need
    return this.getEvaluationStatus(evaluationId).pipe(
      map(status => {
        // Use available results for both running and completed evaluations.
        if (status.results && status.results.length > 0) {
          return this.transformResultsToValidationDetails(evaluationId, status.results);
        }
        
        // No results available yet (common while evaluation is still running).
        return [];
      }),
      catchError(error => {
        return of([]);
      })
    );
  }

  /**
   * Transform the results array from /eval/status into ValidationDetail[] for the UI
   * Handles new API format where fact/value are arrays (consolidated field representation)
   */
  private transformResultsToValidationDetails(evaluationId: string, results: EvaluationFileResult[]): ValidationDetail[] {
    const validationDetails: ValidationDetail[] = [];
    let detailId = 1;
    
    results.forEach(fileResult => {
      const fileName = fileResult.file_name || '';
      const displayFileName = fileName.split('/').pop() || fileName;
      const fieldResults = fileResult.field_results || {};
      
      // Process each field result
      Object.entries(fieldResults).forEach(([fieldName, fieldResult]: [string, FieldResult]) => {
        // Table fields: fact/value are arrays of row-objects (e.g. "Contact Information Table")
        // rather than arrays of primitive strings - handle them separately from the
        // string/list normalization below (row objects have no .trim()).
        if (this.isTableRowArray(fieldResult.fact) || this.isTableRowArray(fieldResult.value)) {
          const expectedTable = this.buildValidationTable(fieldResult.fact);
          const actualTable = this.buildValidationTable(fieldResult.value);

          // Skip fields where ground truth has no rows (field was disabled/not applicable)
          if (expectedTable.rows.length === 0) {
            return;
          }

          validationDetails.push({
            id: detailId++,
            runId: evaluationId,
            fileName: displayFileName,
            field: fieldName,
            expectedValue: `Table (${expectedTable.rows.length} row${expectedTable.rows.length === 1 ? '' : 's'})`,
            actualValue: actualTable.rows.length > 0
              ? `Table (${actualTable.rows.length} row${actualTable.rows.length === 1 ? '' : 's'})`
              : 'N/A',
            match: fieldResult.matched,
            confidence: (fieldResult.score || 0) * 100,
            isTableField: true,
            expectedTable,
            actualTable
          });
          return;
        }

        // Handle new API format where fact/value can be arrays
        let factArray = Array.isArray(fieldResult.fact) ? fieldResult.fact : [fieldResult.fact];
        let valueArray = Array.isArray(fieldResult.value) ? fieldResult.value : [fieldResult.value];
        let pageArray = Array.isArray(fieldResult.page) ? fieldResult.page : [fieldResult.page];

        // Filter out empty strings and N/A from fact array
        const factArrayFiltered = factArray.filter(val =>
          val && val.trim && val.trim() !== '' && val !== 'N/A' && val !== 'NA' && val !== 'n/a'
        );

        // Skip fields where ground truth is empty or all N/A (field was disabled/not applicable)
        if (factArrayFiltered.length === 0) {
          return; // Skip this field - don't show in validation details
        }

        // Convert arrays to comma-separated display strings
        const expectedValue = factArrayFiltered.join(', ');
        const actualValueFiltered = valueArray.filter(val => val && (val.trim ? val.trim() !== '' : true));
        const actualValue = actualValueFiltered.length > 0 ? actualValueFiltered.join(', ') : 'N/A';

        const isMatch = fieldResult.matched;
        const confidence = (fieldResult.score || 0) * 100; // Convert 0-1 to 0-100

        validationDetails.push({
          id: detailId++,
          runId: evaluationId,
          fileName: displayFileName,
          field: fieldName,
          expectedValue: expectedValue,
          actualValue: actualValue,
          match: isMatch,
          confidence: confidence,
          // Store original arrays for tooltip/detailed display
          expectedValueArray: factArrayFiltered as string[],
          actualValueArray: actualValueFiltered as string[],
          pageNumbers: pageArray as number[]
        });
      });
    });
    
    return validationDetails;
  }

  /**
   * Detect whether a field_results fact/value is table-formatted: an array of row-objects
   * (e.g. "Contact Information Table"), as opposed to a plain string or array of strings.
   */
  private isTableRowArray(value: unknown): value is Record<string, any>[] {
    return Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null;
  }

  /**
   * Flatten an array of row-objects into a ValidationDetailTable (columns + stringified rows)
   */
  private buildValidationTable(value: unknown): ValidationDetailTable {
    if (!this.isTableRowArray(value)) {
      return { columns: [], rows: [] };
    }

    const columns = Object.keys(value[0]);
    const rows = value.map(row => {
      const flatRow: Record<string, string> = {};
      columns.forEach(column => {
        const cell = row?.[column];
        flatRow[column] = cell === null || cell === undefined ? '' : String(cell);
      });
      return flatRow;
    });

    return { columns, rows };
  }

  /**
   * Build validation details by comparing extraction results with ground truth
   * @param evaluationId - The evaluation run ID
   * @param fileName - The file being processed
   * @param extractionResult - The extracted data from the evaluation (actual values)
   * @param groundTruth - The ground truth values (expected values)
   */
  private buildValidationDetails(
    evaluationId: string, 
    fileName: string, 
    extractionResult: any, 
    groundTruth: TruthValue[]
  ): ValidationDetail[] {
    // Extract just the filename without the path for display
    const displayFileName = fileName.split('/').pop() || fileName;
    // Log all ground truth entries for debugging
    if (groundTruth.length > 0) {
      groundTruth.forEach((gt: any, i) => {
      });
    }
    
    const validationDetails: ValidationDetail[] = [];
    let detailId = 1;
    
    // Create a map of ground truth values by attribute name (case-insensitive)
    // Also handle indexed keys like "Borrower Sign Dates_1"
    // Handle duplicate keys by storing as array
    const groundTruthMap = new Map<string, TruthValue[]>();
    groundTruth.forEach(gt => {
      const attrName = (gt as any).attributeName || '';
      if (attrName) {
        const normalizedKey = this.normalizeFieldName(attrName);
        const existing = groundTruthMap.get(normalizedKey) || [];
        existing.push(gt);
        groundTruthMap.set(normalizedKey, existing);
      }
    });
    // Track which ground truth fields have been matched
    const matchedFields = new Set<string>();
    
    /**
     * Helper: Lookup ground truth value with fallback support
     * Tries primary key first, then tries alternative keys with/without index
     * @param primaryKey Primary attribute name to look up
     * @param indexSuffix Optional index suffix (e.g., "_1", "_2")
     * @returns TruthValue[] if found, empty array otherwise
     */
    const lookupGroundTruth = (primaryKey: string, indexSuffix?: string): TruthValue[] => {
      if (!primaryKey) return [];
      
      const normalized = this.normalizeFieldName(primaryKey);
      
      // Try 1: Direct lookup with index suffix (e.g., "borrower sign dates_1")
      if (indexSuffix) {
        const keyWithIndex = `${normalized}${indexSuffix}`;
        let result = groundTruthMap.get(keyWithIndex);
        if (result) {
          return result;
        }
      }
      
      // Try 2: Base key (e.g., "borrower sign dates")
      let result = groundTruthMap.get(normalized);
      if (result) {
        return result;
      }
      
      // Try 3: Try without spaces (sometimes external_key doesn't have spaces)
      const noSpaces = normalized.replace(/\s+/g, '');
      for (const [key, value] of groundTruthMap.entries()) {
        if (key.replace(/\s+/g, '') === noSpaces) {
          return value;
        }
      }
      
      // Try 4: Partial match (in case of field_name vs external_key mismatch)
      for (const [key, value] of groundTruthMap.entries()) {
        if (key.includes(normalized) || normalized.includes(key)) {
          return value;
        }
      }
      return [];
    };
    
    // Parse the extraction result - look for the 'attributes' field which contains the actual extracted data
    if (extractionResult && typeof extractionResult === 'object') {
      // The extraction response has an 'attributes' field with the actual extracted values
      const attributes = extractionResult.attributes;
      
      if (attributes) {
        // Parse the attributes - could be a string (JSON) or already an object
        let parsedAttributes: any = {};
        if (typeof attributes === 'string') {
          try {
            parsedAttributes = JSON.parse(attributes);
          } catch (e) {
            parsedAttributes = {};
          }
        } else if (typeof attributes === 'object') {
          parsedAttributes = attributes;
        }
        // Process each attribute (e.g., "Borrower Names", "Borrower Signatures", "Borrower Sign Dates")
        Object.entries(parsedAttributes).forEach(([attrName, attrValue]: [string, any]) => {
          // attrValue is typically an array of extracted values - create separate rows for each
          if (Array.isArray(attrValue) && attrValue.length > 0) {
            // Create a row for each value in the array (e.g., Borrower Names_1, Borrower Names_2)
            attrValue.forEach((item: any, index: number) => {
              const fieldName = `${attrName}_${index + 1}`;
              let actualValue = item.Value || item.value || '';
              
              // Handle empty values - show as N/A
              if (!actualValue || actualValue.trim() === '') {
                actualValue = 'N/A';
              }
              
              // Get confidence/similarity score
              const confidence = (item['Similarity Score'] || item.similarity_score || item.confidence || 0) * 100;
              
              // Find matching ground truth using fallback lookup
              const indexSuffix = `_${index + 1}`;
              const gtValues = lookupGroundTruth(attrName, indexSuffix);
              
              let expectedValue = 'N/A';
              if (gtValues && gtValues.length > 0) {
                // If we have indexed lookup, use first result
                expectedValue = this.toStringValue(gtValues[0].value) || 'N/A';
              } else {
                // Try base lookup without index
                const baseGtValues = lookupGroundTruth(attrName);
                if (baseGtValues && baseGtValues.length > index) {
                  // Get value at this index
                  expectedValue = this.toStringValue(baseGtValues[index].value) || 'N/A';
                } else if (baseGtValues && baseGtValues.length > 0) {
                  // Fallback to first value if index is out of range
                  expectedValue = this.toStringValue(baseGtValues[0].value) || 'N/A';
                }
              }
              // Determine if values match
              const normalizedActual = actualValue.trim().toLowerCase().replace(/\s+/g, ' ');
              const normalizedExpected = this.toStringValue(expectedValue).trim().toLowerCase().replace(/\s+/g, ' ');
              
              // Match logic: both N/A = match, or values are equal/similar
              let isMatch = false;
              if (normalizedActual === 'n/a' && normalizedExpected === 'n/a') {
                isMatch = true;
              } else if (normalizedActual !== 'n/a' && normalizedExpected !== 'n/a') {
                // Both have values - check for match (case-insensitive, handle date formats)
                isMatch = normalizedActual === normalizedExpected ||
                         normalizedActual.includes(normalizedExpected) ||
                         normalizedExpected.includes(normalizedActual) ||
                         this.datesMatch(actualValue, this.toStringValue(expectedValue));
              }
              
              const normalizedAttrName = this.normalizeFieldName(attrName);
              matchedFields.add(`${normalizedAttrName}${indexSuffix}`);
              matchedFields.add(normalizedAttrName);
              
              validationDetails.push({
                id: detailId++,
                runId: evaluationId,
                fileName: displayFileName,
                field: fieldName,
                expectedValue: this.toStringValue(expectedValue),
                actualValue: actualValue,
                match: isMatch,
                confidence: confidence
              });
            });
          } else if (attrValue && typeof attrValue === 'object') {
            // Single object value
            let actualValue = attrValue.Value || attrValue.value || 'N/A';
            const confidence = (attrValue['Similarity Score'] || attrValue.similarity_score || attrValue.confidence || 0) * 100;
            
            // Use fallback lookup helper
            const gtValues = lookupGroundTruth(attrName);
            const expectedValue = (gtValues && gtValues.length > 0) ? this.toStringValue(gtValues[0].value) || 'N/A' : 'N/A';
            
            const normalizedActual = actualValue.trim().toLowerCase();
            const normalizedExpected = this.toStringValue(expectedValue).trim().toLowerCase();
            const isMatch = normalizedActual === normalizedExpected || 
                           (normalizedActual === 'n/a' && normalizedExpected === 'n/a') ||
                           this.datesMatch(actualValue, this.toStringValue(expectedValue));
            
            const normalizedAttrName = this.normalizeFieldName(attrName);
            matchedFields.add(normalizedAttrName);
            
            validationDetails.push({
              id: detailId++,
              runId: evaluationId,
              fileName: displayFileName,
              field: attrName,
              expectedValue: this.toStringValue(expectedValue),
              actualValue: actualValue,
              match: isMatch,
              confidence: confidence
            });
          }
        });
      } else {
        // Fallback: if no 'attributes' field, log a warning
      }
    }
    
    // Add ground truth fields that weren't in extraction results (not extracted)
    
    // Helper function to safely convert string | string[] to string
    const toStringValue = (value: string | string[] | undefined | null): string => {
      if (!value) return '';
      if (typeof value === 'string') return value.trim();
      if (Array.isArray(value)) return value.length > 0 ? String(value[0]).trim() : '';
      return '';
    };
    
    groundTruth.forEach(gt => {
      const attrName = (gt as any).attributeName || '';
      const normalizedAttrName = this.normalizeFieldName(attrName);
      if (attrName && !matchedFields.has(normalizedAttrName)) {
        validationDetails.push({
          id: detailId++,
          runId: evaluationId,
          fileName: displayFileName,
          field: attrName,
          expectedValue: this.toStringValue(gt.value) || 'N/A',
          actualValue: 'Not extracted',
          match: false,
          confidence: 0
        });
      }
    });
    return validationDetails;
  }

  /**
   * Helper: Safely convert value to string, handling both string and string[] types
   */
  private toStringValue(value: string | string[] | TableData | undefined | null): string {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.length > 0 ? String(value[0]).trim() : '';
    // TableData (object/dict) - return empty string (not a simple scalar)
    if (typeof value === 'object') return '';
    return '';
  }

  /**
   * Helper method to compare dates in different formats
   * e.g., "09/30/2025" vs "09-30-2025" or "9/30/2025"
   */
  private datesMatch(date1: string, date2: string): boolean {
    if (!date1 || !date2) return false;
    
    // Normalize dates by removing separators and comparing
    const normalize = (d: string) => d.replace(/[-\/\.]/g, '').replace(/^0+/, '').toLowerCase().trim();
    const n1 = normalize(date1);
    const n2 = normalize(date2);
    
    if (n1 === n2) return true;
    
    // Try parsing as dates and comparing
    try {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      if (!isNaN(d1.getTime()) && !isNaN(d2.getTime())) {
        return d1.getTime() === d2.getTime();
      }
    } catch {
      // Parsing failed, dates don't match
    }
    
    return false;
  }

  /**
   * Compare extracted values against truth values for a single file
   */
  private compareExtractedVsTruth(evaluationId: string, fileName: string, extracted: TruthValue[], truth: TruthValue[]): ValidationDetail[] {
    const validationDetails: ValidationDetail[] = [];
    let detailId = 1;
    
    // Create validation details for each extracted attribute
    extracted.forEach(extractedValue => {
      const truthValue = truth.find(t => 
        t.attributeId === extractedValue.attributeId || 
        (t as any).attributeName === (extractedValue as any).attributeName
      );
      
      const expectedValue = this.toStringValue(truthValue?.value) || 'N/A';
      const actualValue = this.toStringValue(extractedValue.value) || '';
      const match = truthValue ? this.toStringValue(expectedValue).trim() === this.toStringValue(actualValue).trim() : false;
      const fieldName = (extractedValue as any).attributeName || `Attribute ${extractedValue.attributeId}`;
      
      validationDetails.push({
        id: detailId++,
        runId: evaluationId,
        fileName: fileName,
        field: fieldName,
        expectedValue: this.toStringValue(expectedValue),
        actualValue: this.toStringValue(actualValue),
        match: match,
        confidence: 95.0
      });
    });
    
    // Also add validation details for truth values that weren't extracted
    truth.forEach(truthValue => {
      const wasExtracted = extracted.find(e => 
        e.attributeId === truthValue.attributeId ||
        (e as any).attributeName === (truthValue as any).attributeName
      );
      
      if (!wasExtracted) {
        const fieldName = (truthValue as any).attributeName || `Attribute ${truthValue.attributeId}`;
        validationDetails.push({
          id: detailId++,
          runId: evaluationId,
          fileName: fileName,
          field: fieldName,
          expectedValue: this.toStringValue(truthValue.value) || '',
          actualValue: 'Not extracted',
          match: false,
          confidence: 0
        });
      }
    });
    return validationDetails;
  }

  // Excel import method - no localhost backend available
  importExcel(documentCode: string): Observable<any> {
    // Return an error observable since there's no localhost backend
    return of({ error: 'No localhost backend available for Excel import' }).pipe(
      map(() => {
        throw new Error('Excel import requires localhost backend which is not available');
      })
    );
  }

  // Swagger API Methods
  // Note: Authorization headers are automatically added by the HTTP Interceptor
  
  private getAuthHeaders() {
    return {
      headers: {
        'accept': 'application/json'
        // Authorization header is added by authInterceptor
      }
    };
  }
  private getFormAuthHeaders() {
    return {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded'
        // Authorization header is added by authInterceptor
      }
    };
  }
  /**
   * GET /api/v1/admin/documents
   * Get all supported document types from Swagger API
   * @param clientId - Client ID to filter document models for specific client (required)
   */
  /**
   * GET /api/v1/admin/clients/{client_id}/metadata
   * Get metadata (including package_attributes) for a client by ID or name.
   */
  /**
   * POST /api/v1/sequencing/index_and_extract_files
   * Upload a PDF or ZIP to client storage, then delegate to the index_and_extract flow.
   */
  indexAndExtractFiles(file: globalThis.File, clientName: string, metadata: Record<string, unknown>): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('client_name', clientName);
    formData.append('metadata', JSON.stringify(metadata ?? {}));
    formData.append('callback_url', '');

    const url = `${this.apiUrlV1}/sequencing/index_and_extract_files`;
    return this.http.post<any>(url, formData, {
      headers: { accept: 'application/json' },
    }).pipe(
      catchError(error => {
        return throwError(() => error);
      }),
    );
  }

  getClientMetadata(clientId: string): Observable<{
    client_id: string;
    client_name: string;
    metadata: { package_attributes: Array<Record<string, unknown>>; [key: string]: unknown };
    sequencing_enabled?: boolean;
    extraction_enabled?: boolean;
    [key: string]: unknown;
  }> {
    const url = `${this.apiUrlV1}/admin/clients/${encodeURIComponent(clientId)}/metadata`;
    return this.http.get<{
      client_id: string;
      client_name: string;
      metadata: { package_attributes: Array<Record<string, unknown>>; [key: string]: unknown };
      sequencing_enabled?: boolean;
      extraction_enabled?: boolean;
      [key: string]: unknown;
    }>(url, this.getAuthHeaders()).pipe(
      catchError(error => {
        return throwError(() => error);
      }),
    );
  }

  getAdminDocuments(clientId: string): Observable<AdminDocument[]> {
    const params = new HttpParams().set('client_id', clientId);
    return this.http.get<AdminDocument[]>(
      `${this.apiUrlV1}/admin/documents`,
      { 
        ...this.getAuthHeaders(),
        params
      }
    ).pipe(
      map(docs => {
        return docs;
      }),
      catchError(error => {
        return of([]);
      })
    );
  }

  /**
   * GET /api/v1/admin/documents/{document_model}/schema
   * Get the extraction schema for a document model (includes prompts)
   */
  getDocumentSchema(documentModel: string): Observable<any> {
    // Return cached Observable if one exists (deduplicates concurrent calls).
    // The entry is cleared by invalidateDocumentModelCache() on navigate-away
    // and after any schema mutation, so callers always receive fresh data.
    if (this.documentSchemaCache.has(documentModel)) {
      return this.documentSchemaCache.get(documentModel)!;
    }
    const obs$ = this.http.get<any>(
      `${this.apiUrlV1}/admin/documents/${encodeURIComponent(documentModel)}/schema`,
      this.getAuthHeaders()
    ).pipe(
      map(schema => {
        return schema;
      }),
      catchError(error => {
        // Don't cache failures — next call will retry the HTTP request
        this.documentSchemaCache.delete(documentModel);
        return of(null);
      }),
      shareReplay(1)
    );
    this.documentSchemaCache.set(documentModel, obs$);
    return obs$;
  }

  /**
   * POST /api/v1/data/document_keys
   * Get document keys for a specific document type
   */
  getDocumentKeys(documentCode: string): Observable<DocumentKeysResponse> {
    // Return cached Observable if one exists (deduplicates concurrent calls).
    // The entry is cleared by invalidateDocumentModelCache() on navigate-away
    // and after any schema mutation, so callers always receive fresh data.
    if (this.documentKeysCache.has(documentCode)) {
      return this.documentKeysCache.get(documentCode)!;
    }
    // Use form-urlencoded format as required by the API
    // API uses 'document_model' parameter (not document_type)
    // Add response_format=json to get objects instead of strings
    // Add include_extraction_schema=true to get prompt/description data
    const body = `document_model=${encodeURIComponent(documentCode)}&include_internal_key=false&response_format=json&include_extraction_schema=true`;
    const obs$ = this.http.post<any>(
      `${this.apiUrlV1}/data/document_keys`,
      body,
      this.getFormAuthHeaders()
    ).pipe(
      map(response => {
        // Handle API response format - extract keys array
        let keys: any[] = [];
        
        // Check for extraction_schema.properties format (new API format)
        if (response && response.extraction_schema && response.extraction_schema.properties) {
          const properties = response.extraction_schema.properties;
          keys = Object.entries(properties).map(([name, schema]: [string, any]) => {
            const promptValue = schema.items?.description || schema.description;
            
            return {
              field_name: name,
              key_name: name,
              key_label: name,
              type: schema.type,
              is_array: schema.type === 'array',
              description: schema.description,
              items: schema.items,
              prompt: promptValue,
              ld_feature: schema.ld_feature,
              feature: schema.ld_feature || (schema.enum ? 'enum' : undefined),
              allowed_values: schema.enum || schema.allowed_values || schema.items?.enum || [],
              allowed_values_description: schema.allowed_values_description || schema.items?.allowed_values_description || []
            };
          });
        } else if (response && response.keys && Array.isArray(response.keys)) {
          keys = response.keys;
          // Check if keys are objects or strings
          if (keys.length > 0) {
            if (typeof keys[0] === 'string') {
            } else {
            }
          }
        } else if (response && Array.isArray(response)) {
          keys = response;
        }
        
        // Log first key for debugging
        if (keys.length > 0) {
          if (typeof keys[0] === 'object') {
          }
        }
        
        // Map to DocumentKeyRaw format (may contain dicts)
        const mappedKeys = keys.map((key: any, index: number) => ({
          id: key.id || index + 1,
          document_code: documentCode,
          key_name: key.key_name || key.field_name || key.llmde_field_name || key.name || key,
          key_label: key.key_label || key.field_name || key.llmde_field_name || key.label || key.key_name || key.name || key,
          data_type: key.data_type || key.type || 'string',
          is_required: key.is_required || false,
          is_active: key.is_active !== false,
          created_at: key.created_at || new Date().toISOString(),
          updated_at: key.updated_at || new Date().toISOString(),
          // Preserve additional fields from API
          feature: key.feature || key.ld_feature || (key.enum || key.allowed_values ? 'enum' : undefined),
          is_array: key.is_array || key.type === 'array',
          llmde_field_name: key.llmde_field_name,
          field_name: key.field_name, // Preserve original field_name (may be dict)
          prompt: key.prompt || (key.items?.description) || key.description,
          description: key.description,
          items: key.items,
          allowed_values: key.allowed_values || key.enum || key.items?.enum || [],
          allowed_values_description: key.allowed_values_description || []
        }));
        if (mappedKeys.length > 0) {
        }
        
        return {
          document_code: documentCode,
          keys: this.expandDictFieldNames(mappedKeys)
        };
      }),
      catchError(error => {
        // Don't cache failures — next call will retry the HTTP request
        this.documentKeysCache.delete(documentCode);
        return of({ document_code: documentCode, keys: [] });
      }),
      // Cache the resolved Observable so concurrent callers within one page load share the request.
      shareReplay(1)
    );
    this.documentKeysCache.set(documentCode, obs$);
    return obs$;
  }
  /**
   * Expand dict field_name entries into multiple string entries
   * Handles array fields with idx templates like {"0": "Borrower_CAIVRS", "default": "Borrower{idx}_CAIVRS"}
   * Input: DocumentKeyRaw[] (may contain dicts)
   * Output: DocumentKey[] (guaranteed strings)
   */
  private expandDictFieldNames(keys: any[]): DocumentKey[] {
    const expandedKeys: DocumentKey[] = [];
    for (const key of keys) {
      // Check if key_name or field_name is a dict object (not array, not null)
      const keyName = key.key_name;
      const fieldName = key.field_name;
      
      // Determine if we need to expand (either key_name or field_name is a dict)
      const isDictKeyName = typeof keyName === 'object' && keyName !== null && !Array.isArray(keyName);
      const isDictFieldName = typeof fieldName === 'object' && fieldName !== null && !Array.isArray(fieldName);
      if (isDictKeyName || isDictFieldName) {
        // Use field_name dict if available, otherwise use key_name dict
        const dictKey = (isDictFieldName ? fieldName : keyName) as { [key: string]: string };
        // Generate entries for array indices (max 2, matching backend behavior)
        for (let idx = 0; idx < 2; idx++) {
          // Get template: try specific index first, fall back to 'default'
          const template = dictKey[idx.toString()] || dictKey['default'];
          
          if (template) {
            // Replace {idx} placeholder with actual index (1-based)
            const expandedName = template.replace(/\{idx\}/g, (idx + 1).toString());
            
            // Create new entry WITHOUT spreading original key to avoid dict pollution
            const expandedEntry: DocumentKey = {
              id: key.id * 1000 + idx,
              document_code: key.document_code,
              key_name: expandedName,
              key_label: expandedName,
              field_name: expandedName,
              llmde_field_name: key.llmde_field_name,
              data_type: key.data_type,
              is_required: key.is_required,
              is_active: key.is_active,
              created_at: key.created_at,
              updated_at: key.updated_at,
              prompt: key.prompt,
              description: key.description,
              is_array: false, // Each expanded entry is not an array
              items: key.items,
              feature: key.feature,
              allowed_values: key.allowed_values,
              allowed_values_description: key.allowed_values_description
            };
            
            expandedKeys.push(expandedEntry);
          }
        }
      } else {
        // Normal string field_name - create clean entry
        const cleanEntry: DocumentKey = {
          id: key.id,
          document_code: key.document_code,
          key_name: typeof keyName === 'string' ? keyName : (key.llmde_field_name || ''),
          key_label: typeof keyName === 'string' ? keyName : (key.llmde_field_name || ''),
          field_name: typeof fieldName === 'string' ? fieldName : undefined,
          llmde_field_name: key.llmde_field_name,
          data_type: key.data_type,
          is_required: key.is_required,
          is_active: key.is_active,
          created_at: key.created_at,
          updated_at: key.updated_at,
          prompt: key.prompt,
          description: key.description,
          is_array: key.is_array,
          items: key.items,
          feature: key.feature,
          allowed_values: key.allowed_values,
          allowed_values_description: key.allowed_values_description
        };
        expandedKeys.push(cleanEntry);
      }
    }
    if (expandedKeys.length > 0) {
    }
    
    return expandedKeys;
  }
  
  /**
   * Get all document keys for multiple documents
   */
  getAllDocumentKeys(documentCodes: string[]): Observable<DocumentKeysResponse[]> {
    const requests = documentCodes.map(code => this.getDocumentKeys(code));
    return forkJoin(requests);
  }

  /**
   * ============================================
   * PROMPT TUNING API METHODS
   * ============================================
   */

  /**
   * POST /api/v1/document/{document_model}/ai-suggest
   * Suggest field schema guidance using AI with full conversation history.
   */
  suggestFieldWithAi(
    documentModel: string,
    messages: AiSuggestMessage[],
    image?: globalThis.File | Blob | null,
    imageFileName: string = 'context.png'
  ): Observable<AiSuggestResponse> {
    const safeModel = String(documentModel || '').trim();
    if (!safeModel) {
      return throwError(() => new Error('Document model is required for ai-suggest.'));
    }

    const formData = new FormData();
    formData.append('messages', JSON.stringify(messages || []));

    if (image) {
      const imageType = (image as Blob).type || '';
      if (imageType !== 'image/png' && imageType !== 'image/jpeg') {
        return throwError(() => new Error('ai-suggest image must be PNG or JPEG.'));
      }
      formData.append('image', image, imageFileName);
    }

    const url = `${this.apiUrlV1}/document/${encodeURIComponent(safeModel)}/ai-suggest`;
    return this.http.post<AiSuggestResponse>(url, formData, {
      headers: {
        accept: 'application/json'
      }
    }).pipe(
      map((response: AiSuggestResponse) => response || {}),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * GET /api/v1/document/{document_model}/field_schema
   * Get all field schemas for a document model
   */
  getFieldSchemas(documentModel: string): Observable<any> {
    // Return cached Observable if one exists (deduplicates concurrent calls)
    if (this.fieldSchemaCache.has(documentModel)) {
      return this.fieldSchemaCache.get(documentModel)!;
    }
    const obs$ = this.http.get<any>(
      `${this.apiUrlV1}/document/${encodeURIComponent(documentModel)}/field_schema`,
      this.getAuthHeaders()
    ).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        // Don't cache failures
        this.fieldSchemaCache.delete(documentModel);
        this.notificationService.showError('Failed to load field schemas. Please try again.');
        return of([]);
      }),
      shareReplay(1) // Share result with concurrent callers
    );

    this.fieldSchemaCache.set(documentModel, obs$);
    return obs$;
  }

  /**
   * GET /api/v1/document/{document_model}/{field_name}/history
   * Get version history for a specific field (last 10 versions)
   */
  getFieldHistory(documentModel: string, fieldName: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlV1}/document/${encodeURIComponent(documentModel)}/${encodeURIComponent(fieldName)}/history`,
      this.getAuthHeaders()
    ).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        // Don't show notification here - component will handle it with specific message
        return throwError(() => error);
      })
    );
  }

  /**
   * PUT /api/v1/document/{document_model}/{field_name}
   * Update field schema with change reason
   */
  updateFieldSchema(
    documentModel: string,
    fieldName: string,
    changeReason: string,
    fieldSchema: any
  ): Observable<any> {
    const body = {
      change_reason: changeReason,
      field_schema: fieldSchema
    };
    return this.http.put<any>(
      `${this.apiUrlV1}/document/${encodeURIComponent(documentModel)}/${encodeURIComponent(fieldName)}`,
      body,
      this.getAuthHeaders()
    ).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        // Don't show notification here - component will handle it with specific message
        return throwError(() => error);
      })
    );
  }

  /**
   * GET /api/v1/eval/files
   * Get all evaluation files from the Swagger API
   */
  getEvaluationFiles(documentType?: string, evalType?: string): Observable<EvaluationFile[]> {
    let url = `${this.apiUrlV2}/eval/files`;
    const params: string[] = [];
    
    if (documentType) {
      params.push(`document_model=${encodeURIComponent(documentType)}`);
    }
    if (evalType) {
      params.push(`eval_type=${encodeURIComponent(evalType)}`);
    }
    
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    return this.http.get<any>(
      url,
      this.getAuthHeaders()
    ).pipe(
      map(response => {
        // Handle different response formats
        let files: EvaluationFile[] = [];
        if (response && Array.isArray(response)) {
          files = response;
        } else if (response && response.files && Array.isArray(response.files)) {
          files = response.files;
        } else if (response && response.data && Array.isArray(response.data)) {
          files = response.data;
        } else if (response && typeof response === 'object') {
          // Check if response itself has file properties
        }
        // Log first file structure to debug file_id mapping
        if (files.length > 0) {
        }
        
        return files;
      }),
      catchError(error => {
        if (error.error) {
        }
        return of([]);
      })
    );
  }
  /**
   * Get the latest evaluation ID for a document type
   */
  getLatestEvaluationId(documentType?: string): Observable<string> {
    return this.getEvaluationFiles(documentType).pipe(
      map(files => {
        if (files && files.length > 0) {
          // Sort by creation date or ID to get the latest
          const sortedFiles = files.sort((a, b) => {
            const dateA = new Date(a.created_at || 0).getTime();
            const dateB = new Date(b.created_at || 0).getTime();
            return dateB - dateA; // Latest first
          });
          
          const latestFile = sortedFiles[0];
          // Return the file UUID or generate from timestamp
          return String(latestFile.uuid || `eval_${Date.now()}`);
        }
        
        // Fallback to a default evaluation ID
        return `eval_${Date.now()}`;
      }),
      catchError(error => {
        return of(`eval_${Date.now()}`);
      })
    );
  }
  /**
   * Create evaluation file record in Swagger API (metadata only, not file upload)
   * POST /api/v1/eval/files
   * @param fileName The name of the file
   * @param evalType Evaluation type (e.g., 'data_extraction', 'sequencing')
   * @param documentModel Optional document model/type
   */
  uploadEvaluationFile(fileName: string, evalType: string, documentModel?: string): Observable<any> {
    const requestBody: any = {
      file_name: fileName,
      eval_type: evalType
    };
    
    if (documentModel) {
      requestBody.document_model = documentModel;
    }
    return this.http.post(`${this.apiUrlV1}/eval/files`, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
        // Authorization header is added by authInterceptor
      }
    }).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }
  /**
   * Upload actual PDF file content to Azure Blob Storage
   * PUT /api/v1/eval/files/{file_id}/upload
   * @param fileId The file ID (UUID from metadata record)
   * @param file The actual PDF file to upload
   */
  uploadEvaluationFileContent(fileId: string, file: globalThis.File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.http.put(`${this.apiUrlV1}/eval/files/${fileId}/upload`, formData, {
      headers: {
        'accept': 'application/json'
        // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        // Authorization header is added by authInterceptor
      }
    }).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }
  /**
   * Get evaluation file by ID from Swagger API
   * GET /api/v1/eval/files/{file_id}
   * @param fileId File ID
   */
  getEvaluationFileById(fileId: string | number): Observable<any> {
    return this.http.get(`${this.apiUrlV1}/eval/files/${fileId}`, {
      headers: {
        'accept': 'application/json'
      }
    }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }
  /**
   * Stream evaluation file (PDF)
   * GET /api/v1/eval/files/{file_id}/stream
   * @param fileId The file ID to stream
   * @returns Blob URL for the PDF
   */
  streamEvaluationFile(fileId: string): Observable<string> {
    const url = `${this.apiUrlV1}/eval/files/${encodeURIComponent(fileId)}/stream`;
    
    return this.http.get(url, {
      headers: {
        'accept': 'application/pdf'
      },
      responseType: 'blob'
    }).pipe(
      map(blob => {
        const blobUrl = URL.createObjectURL(blob);
        return blobUrl;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  // ============================================
  // DATA EXTRACTION API METHODS
  // ============================================

  /**
   * Submit file for data extraction
   * POST /api/v1/data/extract
   * @param documentModel Document model/code
   * @param fileBlob File blob or File object to extract data from
   * @param fileName Optional file name
   * @param llmModel Optional LLM model to use for extraction (sent as 'model' to backend)
   * @param ocrMode Optional OCR mode to use for text recognition
   * @returns Observable with request_id for polling
   */
  submitDataExtraction(documentModel: string, fileBlob: Blob | globalThis.File, fileName?: string, llmModel?: string, ocrMode?: string): Observable<any> {
    const formData = new FormData();
    const providedFileName = (fileName || '').trim();
    const blobFileName = fileBlob instanceof File ? (fileBlob.name || '').trim() : '';
    const resolvedFileName = providedFileName || blobFileName || 'unknown-file.pdf';
    formData.append('file', fileBlob, resolvedFileName);
    formData.append('document_model', documentModel);
    formData.append('map_external_fields', 'false');
    
    // Add optional LLM model and OCR mode if provided
    if (llmModel) {
      formData.append('model', llmModel);
    }
    if (ocrMode) {
      formData.append('ocr_mode', ocrMode);
    }
    
    const url = `${this.apiUrlV1}/data/extract`;
    return this.http.post<any>(url, formData, {
      headers: {
        'accept': 'application/json'
        // Don't set Content-Type - let browser handle multipart/form-data with boundary
        // Authorization header is added by authInterceptor
      }
    }).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        if (error.error && error.error.detail) {
        }
        throw error;
      })
    );
  }

  /**
   * Poll extraction status
   * GET /api/v1/data/poll_extract?request_id={id}
   * @param requestId Request ID from submitDataExtraction
   * @returns Observable with status and attributes (when COMPLETED)
   */
  pollExtractionStatus(requestId: string): Observable<any> {
    const url = `${this.apiUrlV1}/data/poll_extract`;
    const params = new HttpParams().set('request_id', requestId).set('map_external_fields', 'false');
    
    return this.http.get<any>(url, {
      params,
      headers: {
        'accept': 'application/json'
      }
    }).pipe(
      map(response => {
        if (response.status === 'COMPLETED') {
        }
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
 * Normalize field name for matching
 * Converts to lowercase with single spaces and trimmed
 * Used for matching both field_name and external_key
 * 
 * @param fieldName Field name to normalize
 * @returns Normalized field name
 */
normalizeFieldName(fieldName: string): string {
  if (!fieldName) return '';
  return fieldName
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Build normalized field matching map directly from field_schema API response
 * Creates a multi-key map for attribute lookup using both field_name and external_key
 * Maps are keyed by normalized names for case-insensitive matching
 * 
 * @param fieldSchemaResponse Raw field_schema API response
 * @returns Map<normalizedKey, FieldSchemaField> for O(1) lookups
 */
buildFieldMatchingMapFromSchema(fieldSchemaResponse: FieldSchemaResponse): Map<string, FieldSchemaField> {
  const map = new Map<string, FieldSchemaField>();

  if (!fieldSchemaResponse || !Array.isArray(fieldSchemaResponse.fields)) {
    return map;
  }
  fieldSchemaResponse.fields.forEach((field: FieldSchemaField, index: number) => {
    if (!field.field_name) {
      return;
    }

    // Map field_name (always present and primary key)
    const normalizedFieldName = this.normalizeFieldName(field.field_name);
    map.set(normalizedFieldName, field);
    // Map external_key if present (top-level field in new API)
    if (field.external_key) {
      const normalizedExternalKey = this.normalizeFieldName(field.external_key);
      if (normalizedExternalKey !== normalizedFieldName) {
        map.set(normalizedExternalKey, field);
      }
    }
  });
  return map;
}

/**
 * ============================================
 * SIMPLIFIED ATTRIBUTE MATCHING HELPER
 * ============================================
 * Single method for matching attributes to field schema
 * Handles all matching strategies internally (field_name, external_key, suffix, idx)
 */

/**
 * Strip numeric suffix from normalized field name (e.g., "_1", "_2", etc.)
 * Used for matching array attributes like "Borrower_CAIVRS_1" to base field "Borrower_CAIVRS"
 */
private stripNumericSuffix(normalizedName: string): string {
  return normalizedName.replace(/_\d+$/, '');
}

/**
 * Match an attribute name from Attribute API to a field in Field Schema API
 * 
 * Matching strategies (in order):
 * 1. Direct match: normalized attribute name matches field_name or external_key
 * 2. Suffix-stripped match: remove _1, _2, _3... suffix and compare
 * 3. {idx} expansion: expand {idx} pattern in external_key and match
 * 
 * @param attributeName Attribute name from Attribute API (e.g., "Borrower_CAIVRS_1")
 * @param fieldSchemaOrFields Field schema response OR just the fields array
 * @returns Matched FieldSchemaField or undefined if no match found
 */
matchAttributeToField(
  attributeName: string,
  fieldSchemaOrFields: FieldSchemaResponse | FieldSchemaField[] | { fields: FieldSchemaField[] }
): FieldSchemaField | undefined {
  if (!attributeName) {
    return undefined;
  }

  // Extract fields array from various input formats
  let fields: FieldSchemaField[];
  if (Array.isArray(fieldSchemaOrFields)) {
    fields = fieldSchemaOrFields;
  } else if ('fields' in fieldSchemaOrFields) {
    fields = fieldSchemaOrFields.fields;
  } else {
    return undefined;
  }

  if (!fields || fields.length === 0) {
    return undefined;
  }

  // Build matching map (cached internally by buildFieldMatchingMapFromSchema)
  const matchingMap = this.buildFieldMatchingMapFromSchema({ fields } as FieldSchemaResponse);
  
  // Normalize the attribute name
  const normalized = this.normalizeFieldName(attributeName);
  
  // Strategy 1: Direct lookup (field_name or external_key)
  let field = matchingMap.get(normalized);
  if (field) {
    return field;
  }
  
  // Strategy 2: Strip numeric suffix and try again (_1, _2, _3, etc.)
  const withoutSuffix = this.stripNumericSuffix(normalized);
  if (withoutSuffix !== normalized) {
    field = matchingMap.get(withoutSuffix);
    if (field) {
      return field;
    }
  }
  
  // Strategy 3: Try idx expansion for fields with {idx} pattern in external_key
  // e.g., external_key="Borrower{idx}_CAIVRS" matches "Borrower1_CAIVRS", "Borrower2_CAIVRS", etc.
  for (const f of fields) {
    if (f.external_key?.includes('{idx}')) {
      // Extract numeric index from attribute name
      const idxMatch = attributeName.match(/(\d+)/);
      if (idxMatch) {
        const extractedIdx = idxMatch[1];
        const expanded = f.external_key.replace('{idx}', extractedIdx);
        const normalizedExpanded = this.normalizeFieldName(expanded);
        
        if (normalizedExpanded === normalized || normalizedExpanded === withoutSuffix) {
          return f;
        }
      }
      
      // Try idx=0 (first element, often named without index)
      const expandedIdx0 = f.external_key.replace('{idx}', '');
      const normalizedIdx0 = this.normalizeFieldName(expandedIdx0);
      if (normalizedIdx0 === normalized || normalizedIdx0 === withoutSuffix) {
        return f;
      }
    }
  }
  
  // No match found
  return undefined;
}

/**
 * Get complete field metadata from a FieldSchemaField object
 * Extracts ALL properties needed for UI display in one call
 * 
 * Extracts:
 * - display_name: field_name (for UI)
 * - description: field_schema.description (for i-icon tooltip)
 * - prompt: field_schema.items.description or field_schema.description (detailed guidance)
 * - allowed_values: top-level allowed_values (for dropdown)
 * - is_array: field_datatype === 'array' or field_schema.type === 'array'
 * - feature: field.feature or field_schema.ld_feature (signature, checkbox, etc.)
 * 
 * @param field FieldSchemaField object from field_schema API
 * @returns FieldMetadata with all extracted properties
 * @throws Error if field is null/undefined
 */
getCompleteFieldMetadataFromSchema(field: FieldSchemaField): FieldMetadata {
  if (!field) {
    throw new Error('Cannot extract metadata from null/undefined field');
  }

  // Validate field_schema exists, return defaults if missing
  const fieldSchema = field.field_schema || {};
  
  // Array detection: check field_datatype first (primary), then field_schema.type (fallback)
  const isArray = field.field_datatype === 'array' || fieldSchema.type === 'array';

  // Extract description from field_schema.description (top-level)
  const description = fieldSchema.description || '';

  // Extract prompt from field_schema.items.description (for arrays) or field_schema.description
  const prompt = fieldSchema.items?.description || '';

  // Extract allowed values from top-level array
  const allowedValues = field.allowed_values || [];

  // Extract feature from field.feature or field_schema.ld_feature
  const feature = field.feature || fieldSchema.ld_feature;

  return {
    field_name: field.field_name || '',
    external_key: field.external_key || null,
    display_name: field.field_name || '',        // UI display
    description: description,                     // i-icon tooltip
    prompt: prompt,                               // Detailed guidance
    allowed_values: allowedValues,                // Dropdown options
    is_array: isArray,                            // Array detection
    feature: feature,                             // signature, checkbox, seal, etc.
    field_datatype: field.field_datatype,         // Raw data type
    uuid: field.uuid                              // Field UUID
  };
}

// ============================================
// DATE FIELD VALIDATION & FORMATTING
// ============================================
// Shared MM/DD/YYYY validation/formatting used by both the single-value date
// fields (truth-management component) and table date columns (table-row-input
// component), so both behave identically.

/**
 * A field/column is treated as a date field when its name contains "date"
 */
isDateFieldName(name: string): boolean {
  return String(name || '').toLowerCase().includes('date');
}

/**
 * Validate date in strict MM/DD/YYYY or MM-DD-YYYY format (2-digit month, 2-digit day,
 * 4-digit year). No other format (ISO, YYYY/MM/DD, 2-digit year, free text, etc.) is accepted.
 */
isValidDateFormat(value: string): boolean {
  if (!value) return false;

  const slashFormat = /^\d{2}\/\d{2}\/\d{4}$/.test(value);
  const dashFormat = /^\d{2}-\d{2}-\d{4}$/.test(value);
  if (!slashFormat && !dashFormat) return false;

  const parts = value.split(slashFormat ? '/' : '-');
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (month < 1 || month > 12) return false;
  if (year < 1900 || year > 2100) return false;

  const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  const daysInMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day >= 1 && day <= daysInMonth[month - 1];
}

/**
 * Pad a typed M/D/YYYY or M-D-YYYY value (single-digit month/day) to strict MM/DD/YYYY,
 * converting a dash separator to slash for consistent storage/display. No other format
 * (ISO, YYYY/MM/DD, 2-digit year, free text, etc.) is normalized - anything else is
 * returned unchanged so isValidDateFormat correctly rejects it, rather than silently
 * reinterpreting it into a different date (e.g. new Date("12/12/20") => year 2020).
 */
normalizeDateToMMDDYYYY(value: string): string {
  if (!value || !value.trim()) return '';

  const trimmed = value.trim();

  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const parts = trimmed.split('/');
    return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2]}`;
  }

  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmed)) {
    const parts = trimmed.split('-');
    return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2]}`;
  }

  return trimmed;
}

/**
 * Date formatting helper - MM/DD/YYYY format (e.g., 07/22/2000)
 */
formatDateForDisplay(value: string): string {
  if (!value) return '';
  // If already in MM/DD/YYYY format (strict 2-digit month and day), return as is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
  // If in M/D/YYYY format, convert to MM/DD/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    const parts = value.split('/');
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    const year = parts[2];
    return `${month}/${day}/${year}`;
  }
  // Convert from yyyy-mm-dd (date picker format) to MM/DD/YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${month}/${day}/${year}`;
  }
  // Try to parse other date formats
  const date = new Date(value);
  if (!isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }
  return value;
}

/**
 * Build the range-specific error message for an invalid MM/DD/YYYY date
 */
getDateValidationErrorMessage(normalized: string): string {
  let errorMsg = 'Invalid date format. Please use MM/DD/YYYY.';
  const parts = normalized.split('/');
  if (parts.length === 3) {
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (month < 1 || month > 12) {
      errorMsg = 'Invalid month. Month must be between 01 and 12.';
    } else if (year < 1900 || year > 2100) {
      errorMsg = 'Invalid year. Year must be between 1900 and 2100.';
    } else if (day < 1 || day > 31) {
      errorMsg = 'Invalid day. Day must be valid for the given month.';
    }
  }
  return errorMsg;
}

/**
 * Validate and correct date digits as they're typed, so the value being built
 * can never end up with an impossible month/day (e.g. "13" or "00")
 */
validateDateDigits(digits: string): string {
  if (digits.length === 0) return digits;

  let result = '';

  // Validate month (first 2 digits)
  if (digits.length >= 1) {
    const m1 = parseInt(digits[0], 10);
    if (m1 > 1) {
      result = '0' + m1; // Auto-correct: 3 -> 03
      digits = result + digits.substring(1);
    } else {
      result = digits[0];
    }
  }

  if (digits.length >= 2) {
    const month = parseInt(digits.substring(0, 2), 10);
    if (month === 0) {
      result = '01'; // Correct 00 to 01
    } else if (month > 12) {
      result = '12'; // Cap at 12
    } else {
      result = digits.substring(0, 2);
    }
  }

  // Validate day (digits 3-4)
  if (digits.length >= 3) {
    const d1 = parseInt(digits[2], 10);
    if (d1 > 3) {
      result += '0' + d1; // Auto-correct: 5 -> 05
      digits = digits.substring(0, 2) + '0' + d1 + digits.substring(3);
    } else {
      result += digits[2];
    }
  }

  if (digits.length >= 4) {
    const month = parseInt(digits.substring(0, 2), 10);
    const day = parseInt(digits.substring(2, 4), 10);

    let maxDay = 31;
    if ([4, 6, 9, 11].includes(month)) {
      maxDay = 30;
    } else if (month === 2) {
      maxDay = 29;
    }

    if (day === 0) {
      result = result.substring(0, 2) + '01';
    } else if (day > maxDay) {
      result = result.substring(0, 2) + maxDay.toString().padStart(2, '0');
    } else {
      result = digits.substring(0, 4);
    }
  }

  // Year digits (5-8) - just copy as-is
  if (digits.length > 4) {
    result += digits.substring(4, 8);
  }

  return result;
}

/**
 * Block non-numeric input and digits that can never form a valid MM/DD/YYYY date,
 * as the user types
 */
restrictDateKeydown(event: KeyboardEvent): void {
  const key = event.key;

  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
  if (allowedKeys.includes(key)) {
    return;
  }

  if (event.ctrlKey || event.metaKey) {
    return;
  }

  if (!/^\d$/.test(key)) {
    event.preventDefault();
    return;
  }

  const input = event.target as HTMLInputElement;
  const currentDigits = input.value.replace(/[^\d]/g, '');

  if (currentDigits.length >= 8) {
    event.preventDefault();
    return;
  }

  const newDigits = currentDigits + key;
  const digitCount = newDigits.length;

  // Validate month (positions 1-2)
  if (digitCount === 1) {
    const digit = parseInt(key, 10);
    if (digit > 1) {
      event.preventDefault();
      return;
    }
  } else if (digitCount === 2) {
    const firstDigit = parseInt(newDigits[0], 10);
    const secondDigit = parseInt(key, 10);
    if (firstDigit === 0 && secondDigit === 0) {
      event.preventDefault();
      return;
    }
    if (firstDigit === 1 && secondDigit > 2) {
      event.preventDefault();
      return;
    }
  }

  // Validate day (positions 3-4)
  if (digitCount === 3) {
    const digit = parseInt(key, 10);
    if (digit > 3) {
      event.preventDefault();
      return;
    }
  } else if (digitCount === 4) {
    const dayFirstDigit = parseInt(newDigits[2], 10);
    const daySecondDigit = parseInt(key, 10);
    const month = parseInt(newDigits.substring(0, 2), 10);

    if (dayFirstDigit === 0 && daySecondDigit === 0) {
      event.preventDefault();
      return;
    }
    if (dayFirstDigit === 3 && daySecondDigit > 1) {
      event.preventDefault();
      return;
    }

    const day = dayFirstDigit * 10 + daySecondDigit;
    if ([4, 6, 9, 11].includes(month) && day > 30) {
      event.preventDefault();
      return;
    }
    if (month === 2 && day > 29) {
      event.preventDefault();
      return;
    }
  }
}

/**
 * Convert raw typed digits into an auto-formatted MM/DD/YYYY string
 */
formatDigitsToMMDDYYYY(rawValue: string): string {
  let digits = rawValue.replace(/[^\d]/g, '');
  digits = digits.substring(0, 8);
  digits = this.validateDateDigits(digits);

  let formatted = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) {
      formatted += '/';
    }
    formatted += digits[i];
  }
  return formatted;
}

/**
 * Convert an MM/DD/YYYY (or parseable) value to yyyy-mm-dd for the native date picker
 */
getDatePickerValueFromText(value: string): string {
  if (!value) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const parts = value.split('/');
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    const year = parts[2];
    if (this.isValidDateFormat(`${month}/${day}/${year}`)) {
      return `${year}-${month}-${day}`;
    }
  }
  const date = new Date(value);
  if (!isNaN(date.getTime())) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
}

/**
 * Normalize a date value to MM/DD/YYYY for equality comparisons, so equivalent
 * formats (e.g. "1/2/2024" vs "01/02/2024") aren't treated as a modification
 */
normalizeDateForComparison(value: string): string {
  if (!value) return '';
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    const parts = value.split('/');
    return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2]}`;
  }
  const date = new Date(value);
  if (!isNaN(date.getTime())) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  }
  return value;
}

}

